/**
 * Elite Batch 21 Part 4 — 27 Workflow Templates
 * S4 remaining (4): Refund winback, service recovery offers, renewal reminders, unhappy review recovery
 * S5 logistics/appointments (11): Canceled appointment reschedule, delivery delay apology,
 *   exchange support, failed delivery rescue, reservation confirmation, reservation reminder,
 *   return initiated, return received refund, return recovery, service appointment follow-up,
 *   store appointment reminder
 * S6 outage communication (6): Bug incident communication, outage bot, outage notification,
 *   outage resolved follow-up, proactive service alert, service outage notification
 * S7 community/knowledge (6): Forum auto-responder, community moderation, community-support invite,
 *   KB article suggester, KB personal wiki, support ticket to community post
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

export const ELITE_BATCH21_PART4: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ─── S4 REMAINING (1): Refund Customer Winback After Support ───
  "Refund Customer Winback After Support": {
    summary:
      "When a refund is processed, fetches customer profile and refund context from HubSpot, waits a 14-day cooling period, then uses AI to generate a personalized winback message based on the refund reason. High-value customers (LTV > $500) require CSM approval before the winback email is sent. Tracks conversion rates in Google Sheets.",
    nodes: [
      trigger("Refund Processed Webhook", "webhook_app", {
        source: "stripe",
        event: "charge.refunded",
        howItStarts:
          "Fires when Stripe processes a refund for a customer charge",
        setup:
          "Connect your Stripe account and subscribe to the charge.refunded webhook event",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Extract Refund Details",
        280,
        "You are a data extraction assistant. Parse the incoming webhook payload to find the refund amount, customer email, customer name, refund reason, and original charge ID. Return JSON regardless of payload format.",
        "Extract refund details from this webhook payload: {webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "refund_amount", as: "refund_amount", type: "number" },
          { field: "refund_reason", as: "refund_reason", type: "string" },
          { field: "charge_id", as: "charge_id", type: "string" },
        ],
      ),
      n("3", "hitApi", "Fetch Customer Profile from HubSpot", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{customer_email}"}]}]',
            },
            {
              key: "properties",
              value:
                '["email","firstname","lastname","lifecyclestage","hs_object_id","total_revenue"]',
            },
          ],
          variables: [
            {
              name: "results[0].properties.firstname",
              customName: "first_name",
              type: "string",
            },
            {
              name: "results[0].properties.lastname",
              customName: "last_name",
              type: "string",
            },
            {
              name: "results[0].properties.total_revenue",
              customName: "customer_ltv",
              type: "number",
            },
            {
              name: "results[0].properties.lifecyclestage",
              customName: "lifecycle_stage",
              type: "string",
            },
          ],
        },
      }),
      n("4", "delayAction", "Wait 14 Days Cooling Period", 760, {
        delayDetails: { duration: 14, unit: "days" },
      }),
      aiNode(
        "5",
        "AI Generate Personalized Winback Message",
        1000,
        "You are a customer retention specialist. Based on the refund reason, customer lifetime value, and lifecycle stage, craft a personalized winback email. Include a relevant incentive (discount, extended trial, upgrade, or personal onboarding). Return JSON with subject, body, and incentive_type fields.",
        "Customer: {first_name} {last_name}, Email: {customer_email}, LTV: ${customer_ltv}, Refund Reason: {refund_reason}, Refund Amount: ${refund_amount}, Lifecycle: {lifecycle_stage}. Generate a winback email.",
        [
          { field: "subject", as: "winback_subject", type: "string" },
          { field: "body", as: "winback_body", type: "string" },
          { field: "incentive_type", as: "incentive_type", type: "string" },
        ],
      ),
      n("6", "condition", "Customer LTV > $500?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "customer_ltv", operator: "greaterThan", value: "500" },
          ],
        },
      }),
      n("7", "approvalGate", "CSM Approves High-Value Winback", 1480, {
        approvalDetails: {
          approver: "csm_team",
          timeout: 48,
          timeoutUnit: "hours",
        },
      }),
      emailNode(
        "8",
        "Send Winback Email with Incentive",
        1720,
        "{customer_email}",
        "{winback_subject}",
        "{winback_body}",
      ),
      sheetsNode(
        "9",
        "Track Winback Conversion Rate",
        1960,
        "WinbackTracking",
        '[["{customer_email}","{refund_reason}","{incentive_type}","{customer_ltv}","{refund_amount}","sent","{todays_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ─── S4 REMAINING (2): Service Recovery Offer Generator ───
  "Service Recovery Offer Generator": {
    summary:
      "When a negative customer experience is flagged, fetches the customer profile and experience history from HubSpot, uses AI to analyze severity and recommend a recovery offer, applies policy rules via code, routes high-value offers (>$100) through manager approval, then sends the recovery offer to the customer and logs acceptance metrics.",
    nodes: [
      trigger("Negative Experience Flagged Webhook", "webhook_app", {
        source: "zendesk",
        event: "ticket.tagged.negative_experience",
        howItStarts:
          "Fires when a support ticket is tagged with a negative experience flag in Zendesk",
        setup:
          "Configure Zendesk webhook trigger on tickets tagged 'negative_experience'",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Extract Experience Details",
        280,
        "You are a data extraction assistant. Parse the webhook payload to find the customer email, customer name, ticket ID, experience description, severity indicators, and any prior recovery offers mentioned. Return JSON regardless of payload format.",
        "Extract negative experience details from this webhook payload: {webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "ticket_id", as: "ticket_id", type: "string" },
          {
            field: "experience_description",
            as: "experience_description",
            type: "string",
          },
          { field: "severity_level", as: "severity_level", type: "string" },
        ],
      ),
      n("3", "hitApi", "Fetch Customer Profile and Recovery History", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{customer_email}"}]}]',
            },
            {
              key: "properties",
              value:
                '["email","firstname","lastname","total_revenue","last_recovery_date","recovery_count"]',
            },
          ],
          variables: [
            {
              name: "results[0].properties.firstname",
              customName: "first_name",
              type: "string",
            },
            {
              name: "results[0].properties.total_revenue",
              customName: "customer_ltv",
              type: "number",
            },
            {
              name: "results[0].properties.recovery_count",
              customName: "prior_recovery_count",
              type: "number",
            },
          ],
        },
      }),
      aiNode(
        "4",
        "AI Analyze Severity and Recommend Recovery",
        760,
        "You are a customer recovery specialist. Analyze the severity of the negative experience, the customer lifetime value, and prior recovery history. Recommend a recovery offer type (discount, credit, free_month, upgrade, personal_call) and dollar value. Be proportional: higher severity and higher LTV warrant more generous offers. Return JSON with offer_type, offer_value, offer_description, and severity_score (1-10).",
        "Experience: {experience_description}, Severity: {severity_level}, Customer LTV: ${customer_ltv}, Prior recoveries: {prior_recovery_count}. Recommend a recovery offer.",
        [
          { field: "offer_type", as: "offer_type", type: "string" },
          { field: "offer_value", as: "offer_value", type: "number" },
          {
            field: "offer_description",
            as: "offer_description",
            type: "string",
          },
          { field: "severity_score", as: "severity_score", type: "number" },
        ],
      ),
      n("5", "runCode", "Apply Offer Limits and Policy Rules", 1000, {
        codeDetails: {
          language: "javascript",
          code: `// Apply company policy limits on recovery offers
const offerValue = parseFloat(vars.offer_value) || 0;
const priorCount = parseInt(vars.prior_recovery_count) || 0;
const maxPerYear = 3;
const maxSingleOffer = 250;

let finalValue = Math.min(offerValue, maxSingleOffer);
let policyNote = "";

if (priorCount >= maxPerYear) {
  finalValue = Math.min(finalValue, 25);
  policyNote = "Capped at $25 — customer has reached annual recovery limit";
} else if (priorCount >= 2) {
  finalValue = Math.min(finalValue, 50);
  policyNote = "Reduced — customer has had multiple recoveries this year";
} else {
  policyNote = "Within policy limits";
}

return { final_offer_value: finalValue, policy_note: policyNote, needs_approval: finalValue > 100 };`,
          outputs: [
            { name: "final_offer_value", type: "number" },
            { name: "policy_note", type: "string" },
            { name: "needs_approval", type: "boolean" },
          ],
        },
      }),
      n("6", "condition", "Offer Value > $100?", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "final_offer_value",
              operator: "greaterThan",
              value: "100",
            },
          ],
        },
      }),
      n("7", "approvalGate", "Manager Approves Recovery Offer", 1480, {
        approvalDetails: {
          approver: "support_manager",
          timeout: 24,
          timeoutUnit: "hours",
        },
      }),
      emailNode(
        "8",
        "Send Recovery Offer to Customer",
        1720,
        "{customer_email}",
        "We want to make this right, {first_name}",
        "Dear {first_name},\\n\\nWe sincerely apologize for your recent experience. {offer_description}\\n\\nWe have applied a ${final_offer_value} {offer_type} to your account as our commitment to making this right.\\n\\nIf you have any questions or need further assistance, please reply to this email and a senior team member will respond personally.\\n\\nWarm regards,\\nCustomer Experience Team",
      ),
      sheetsNode(
        "9",
        "Track Recovery Offer ROI",
        1960,
        "RecoveryOffers",
        '[["{customer_email}","{ticket_id}","{severity_score}","{offer_type}","{final_offer_value}","{policy_note}","{customer_ltv}","sent","{todays_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ─── S4 REMAINING (3): Service Renewal Reminder ───
  "Service Renewal Reminder": {
    summary:
      "Runs daily to find customers with renewals in the next 60 days. Joins renewal timing with health score and usage data. At-risk customers (health < 50) trigger a Slack alert to the CSM; healthy customers receive an email with usage highlights. After a 30-day delay, a final renewal notice with clear CTA is sent.",
    nodes: [
      trigger("Daily Renewal Check", "schedule", {
        schedule: "Every day at 9:00 AM UTC",
        howItStarts:
          "Runs daily to check for upcoming service renewals in the next 60 days",
        setup: "No external setup needed — runs on schedule",
        inputFields: [],
      }),
      n("2", "hitApi", "Fetch Customers with Renewals in 60 Days", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"renewal_date","operator":"LTE","value":"{sixty_days_from_now}"},{"propertyName":"renewal_date","operator":"GTE","value":"{today}"}]}]',
            },
            {
              key: "properties",
              value:
                '["email","firstname","lastname","renewal_date","health_score","monthly_usage","plan_name","contract_value"]',
            },
            { key: "limit", value: "100" },
          ],
          variables: [
            {
              name: "results",
              customName: "renewal_customers",
              type: "object",
            },
            { name: "total", customName: "total_renewals", type: "number" },
          ],
        },
      }),
      n("3", "runCode", "Join Renewal Timing with Health and Usage", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Process each renewal customer and enrich with context
const customers = vars.renewal_customers || [];
const customer = customers[0] || {};
const props = customer.properties || {};

const daysUntilRenewal = Math.ceil((new Date(props.renewal_date) - new Date()) / (1000 * 60 * 60 * 24));
const healthScore = parseInt(props.health_score) || 50;
const usage = props.monthly_usage || "0";

return {
  customer_email: props.email || "",
  first_name: props.firstname || "Customer",
  plan_name: props.plan_name || "Standard",
  contract_value: props.contract_value || "0",
  health_score: healthScore,
  days_until_renewal: daysUntilRenewal,
  monthly_usage: usage,
  renewal_date: props.renewal_date || "",
  is_at_risk: healthScore < 50
};`,
          outputs: [
            { name: "customer_email", type: "string" },
            { name: "first_name", type: "string" },
            { name: "plan_name", type: "string" },
            { name: "contract_value", type: "string" },
            { name: "health_score", type: "number" },
            { name: "days_until_renewal", type: "number" },
            { name: "monthly_usage", type: "string" },
            { name: "renewal_date", type: "string" },
            { name: "is_at_risk", type: "boolean" },
          ],
        },
      }),
      n("4", "condition", "Health Score < 50?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "health_score", operator: "lessThan", value: "50" },
          ],
        },
      }),
      slackNode(
        "5",
        "Alert CSM — Renewal Risk",
        1000,
        ":warning: *Renewal Risk Alert*\\n• Customer: {first_name} ({customer_email})\\n• Plan: {plan_name} (${contract_value}/yr)\\n• Health Score: {health_score}/100\\n• Renews in: {days_until_renewal} days ({renewal_date})\\n• Monthly Usage: {monthly_usage}\\nPlease engage this customer before renewal.",
        400,
      ),
      emailNode(
        "6",
        "Send Renewal Reminder with Usage Highlights",
        1000,
        "{customer_email}",
        "Your {plan_name} renewal is coming up, {first_name}",
        "Hi {first_name},\\n\\nYour {plan_name} subscription renews on {renewal_date} — that is {days_until_renewal} days from now.\\n\\nHere is a snapshot of your usage this month:\\n• Monthly activity: {monthly_usage} sessions\\n• Plan: {plan_name}\\n\\nWe are glad you are getting value from the platform. If you would like to discuss your renewal or explore plan options, just reply to this email.\\n\\nBest,\\nCustomer Success Team",
      ),
      n("7", "delayAction", "Wait 30 Days", 1240, {
        delayDetails: { duration: 30, unit: "days" },
      }),
      emailNode(
        "8",
        "Send Final Renewal Notice with CTA",
        1480,
        "{customer_email}",
        "Action needed: Your {plan_name} renewal on {renewal_date}",
        "Hi {first_name},\\n\\nThis is a friendly reminder that your {plan_name} subscription renews on {renewal_date}.\\n\\nTo ensure uninterrupted service, please confirm your renewal or update your payment method:\\n\\n[Confirm Renewal →]\\n\\nIf you have questions about your plan or want to explore upgrades, our team is here to help.\\n\\nBest,\\nCustomer Success Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ─── S4 REMAINING (4): Unhappy Review Service Recovery ───
  "Unhappy Review Service Recovery": {
    summary:
      "When a negative review is detected on Google, G2, or Trustpilot, AI extracts the review content and rating, looks up the customer in HubSpot, drafts both a public response and private remediation message, routes 1-2 star reviews through manager approval before the public response is posted, sends private outreach to the customer, and logs recovery outcomes.",
    nodes: [
      trigger("Negative Review Detected Webhook", "webhook_app", {
        source: "reviewtrackers",
        event: "review.negative",
        howItStarts:
          "Fires when a new negative review (3 stars or below) is detected on Google, G2, or Trustpilot",
        setup:
          "Connect your review monitoring tool (ReviewTrackers, Birdeye, or custom webhook) to forward negative reviews",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Extract Review Content and Rating",
        280,
        "You are a review analysis assistant. Parse the webhook payload to extract the review text, star rating (1-5), reviewer name, reviewer email if available, review platform (Google/G2/Trustpilot), and review URL. Return JSON regardless of payload format.",
        "Extract review details from this webhook payload: {webhook_payload}",
        [
          { field: "review_text", as: "review_text", type: "string" },
          { field: "star_rating", as: "star_rating", type: "number" },
          { field: "reviewer_name", as: "reviewer_name", type: "string" },
          { field: "reviewer_email", as: "reviewer_email", type: "string" },
          { field: "review_platform", as: "review_platform", type: "string" },
          { field: "review_url", as: "review_url", type: "string" },
        ],
      ),
      n("3", "hitApi", "Lookup Customer in HubSpot", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{reviewer_email}"}]}]',
            },
            {
              key: "properties",
              value:
                '["email","firstname","lastname","total_revenue","hs_object_id","last_support_ticket_date"]',
            },
          ],
          variables: [
            {
              name: "results[0].properties.firstname",
              customName: "first_name",
              type: "string",
            },
            {
              name: "results[0].properties.lastname",
              customName: "last_name",
              type: "string",
            },
            {
              name: "results[0].properties.total_revenue",
              customName: "customer_ltv",
              type: "number",
            },
            {
              name: "results[0].properties.last_support_ticket_date",
              customName: "last_ticket_date",
              type: "string",
            },
          ],
        },
      }),
      aiNode(
        "4",
        "Draft Public Response and Private Remediation",
        760,
        "You are a customer experience communications expert. Given a negative review, draft two messages: 1) A professional public response that acknowledges the concern, shows empathy, and invites them to connect offline — keep it concise and brand-appropriate. 2) A private remediation email that is more detailed, offers specific help, and includes an incentive or next step. Return JSON with public_response and private_email_subject and private_email_body.",
        'Review on {review_platform}: \\"{review_text}\\" ({star_rating} stars) by {reviewer_name}. Customer LTV: ${customer_ltv}. Last support ticket: {last_ticket_date}. Draft public response and private outreach.',
        [
          { field: "public_response", as: "public_response", type: "string" },
          {
            field: "private_email_subject",
            as: "private_email_subject",
            type: "string",
          },
          {
            field: "private_email_body",
            as: "private_email_body",
            type: "string",
          },
        ],
      ),
      n("5", "condition", "Rating <= 2 Stars?", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "star_rating",
              operator: "lessThanOrEqual",
              value: "2",
            },
          ],
        },
      }),
      n("6", "approvalGate", "Manager Reviews Public Response", 1240, {
        approvalDetails: {
          approver: "customer_experience_manager",
          timeout: 12,
          timeoutUnit: "hours",
        },
      }),
      emailNode(
        "7",
        "Send Private Remediation Outreach",
        1480,
        "{reviewer_email}",
        "{private_email_subject}",
        "{private_email_body}",
      ),
      sheetsNode(
        "8",
        "Log Review Recovery Outcomes",
        1720,
        "ReviewRecovery",
        '[["{reviewer_name}","{reviewer_email}","{review_platform}","{star_rating}","{review_text}","{public_response}","sent","{todays_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ─── S5 LOGISTICS (5): Canceled Appointment Reschedule ───
  "Canceled Appointment Reschedule": {
    summary:
      "When an appointment is canceled in Calendly, AI extracts the cancellation reason and customer timezone, finds next available slots considering the timezone, then sends reschedule options to customer-initiated cancellations, notifies the provider via Slack, and tracks cancellation patterns in Google Sheets.",
    nodes: [
      trigger("Appointment Canceled in Calendly", "webhook_app", {
        source: "calendly",
        event: "invitee.canceled",
        howItStarts:
          "Fires when a scheduled appointment is canceled in Calendly",
        setup:
          "Subscribe to the invitee.canceled webhook event in your Calendly developer settings",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Extract Cancellation Details",
        280,
        "You are a scheduling data extraction assistant. Parse the Calendly cancellation webhook payload to find the customer email, customer name, cancellation reason, original appointment time, event type, provider/host name, and customer timezone. Also determine if this was a customer-initiated or provider-initiated cancellation. Return JSON regardless of payload format.",
        "Extract cancellation details from this Calendly webhook: {webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          {
            field: "cancellation_reason",
            as: "cancellation_reason",
            type: "string",
          },
          { field: "original_time", as: "original_time", type: "string" },
          { field: "event_type", as: "event_type", type: "string" },
          { field: "provider_name", as: "provider_name", type: "string" },
          {
            field: "customer_timezone",
            as: "customer_timezone",
            type: "string",
          },
          {
            field: "customer_initiated",
            as: "customer_initiated",
            type: "boolean",
          },
        ],
      ),
      n("3", "runCode", "Find Next Available Slots by Timezone", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Generate 3 suggested reschedule slots considering customer timezone
const tz = vars.customer_timezone || "America/New_York";
const now = new Date();
const slots = [];

for (let i = 1; i <= 3; i++) {
  const slot = new Date(now.getTime() + (i * 2 + 1) * 24 * 60 * 60 * 1000);
  slot.setHours(10 + i, 0, 0, 0); // 11am, 12pm, 1pm
  slots.push(slot.toISOString());
}

return {
  slot_1: slots[0],
  slot_2: slots[1],
  slot_3: slots[2],
  scheduling_link: "https://calendly.com/your-team/" + (vars.event_type || "meeting").toLowerCase().replace(/\\s+/g, "-"),
  slots_display: slots.map((s, i) => "Option " + (i+1) + ": " + new Date(s).toLocaleString("en-US", { timeZone: tz })).join("\\n")
};`,
          outputs: [
            { name: "slot_1", type: "string" },
            { name: "slot_2", type: "string" },
            { name: "slot_3", type: "string" },
            { name: "scheduling_link", type: "string" },
            { name: "slots_display", type: "string" },
          ],
        },
      }),
      n("4", "condition", "Customer-Initiated Cancellation?", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "customer_initiated",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
      emailNode(
        "5",
        "Send Reschedule Options",
        1000,
        "{customer_email}",
        "Let's reschedule your {event_type}, {customer_name}",
        "Hi {customer_name},\\n\\nWe noticed you canceled your {event_type} that was scheduled for {original_time}. No worries at all!\\n\\nHere are some available times to reschedule:\\n{slots_display}\\n\\nOr pick a time that works best for you: {scheduling_link}\\n\\nLooking forward to connecting with you.\\n\\nBest regards,\\n{provider_name}",
      ),
      slackNode(
        "6",
        "Notify Provider of Cancellation",
        1240,
        ":calendar: *Appointment Canceled*\\n• Customer: {customer_name} ({customer_email})\\n• Event: {event_type}\\n• Original Time: {original_time}\\n• Reason: {cancellation_reason}\\n• Customer initiated: {customer_initiated}\\n• Provider: {provider_name}",
      ),
      sheetsNode(
        "7",
        "Track Cancellation Patterns",
        1480,
        "CancellationTracking",
        '[["{customer_name}","{customer_email}","{event_type}","{original_time}","{cancellation_reason}","{customer_initiated}","{provider_name}","{todays_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ─── S5 LOGISTICS (6): Delivery Delayed Apology Plus Support ───
  "Delivery Delayed Apology Plus Support": {
    summary:
      "When ShipStation detects a delivery delay, fetches order details from Shopify, calculates delay severity and compensation level, sends a proactive delay notification with tracking update. For severe delays (>5 days), sends an urgent SMS alert with support contact. Alerts the fulfillment team via Slack and logs delay recovery metrics.",
    nodes: [
      trigger("ShipStation Delivery Delay Detected", "webhook_app", {
        source: "shipstation",
        event: "SHIP_NOTIFY",
        howItStarts:
          "Fires when ShipStation detects a shipping delay or delivery exception for an order",
        setup:
          "Configure ShipStation webhook for delivery exception notifications",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Extract Delay Details from ShipStation",
        280,
        "You are a shipping data extraction assistant. Parse the ShipStation webhook payload to find the order number, tracking number, carrier name, original estimated delivery date, new estimated delivery date, delay reason, and customer email. Return JSON regardless of payload format.",
        "Extract delivery delay details from this ShipStation webhook: {webhook_payload}",
        [
          { field: "order_number", as: "order_number", type: "string" },
          { field: "tracking_number", as: "tracking_number", type: "string" },
          { field: "carrier", as: "carrier", type: "string" },
          { field: "original_eta", as: "original_eta", type: "string" },
          { field: "new_eta", as: "new_eta", type: "string" },
          { field: "delay_reason", as: "delay_reason", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
        ],
      ),
      n("3", "hitApi", "Fetch Order Details from Shopify", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/orders.json?name={order_number}&fields=id,email,phone,shipping_address,line_items,total_price,customer",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "orders[0].customer.first_name",
              customName: "customer_first_name",
              type: "string",
            },
            {
              name: "orders[0].customer.last_name",
              customName: "customer_last_name",
              type: "string",
            },
            {
              name: "orders[0].phone",
              customName: "customer_phone",
              type: "string",
            },
            {
              name: "orders[0].total_price",
              customName: "order_total",
              type: "string",
            },
            {
              name: "orders[0].line_items[0].title",
              customName: "item_name",
              type: "string",
            },
          ],
        },
      }),
      n("4", "runCode", "Calculate Delay Severity and Compensation", 760, {
        codeDetails: {
          language: "javascript",
          code: `// Calculate delay in days and determine compensation level
const original = new Date(vars.original_eta);
const updated = new Date(vars.new_eta);
const delayDays = Math.ceil((updated - original) / (1000 * 60 * 60 * 24));
const orderTotal = parseFloat(vars.order_total) || 0;

let compensation = "none";
let compensationValue = 0;
let severity = "minor";

if (delayDays > 7) {
  severity = "critical";
  compensationValue = Math.round(orderTotal * 0.20);
  compensation = "20% refund ($" + compensationValue + ")";
} else if (delayDays > 5) {
  severity = "severe";
  compensationValue = Math.round(orderTotal * 0.10);
  compensation = "10% refund ($" + compensationValue + ")";
} else if (delayDays > 3) {
  severity = "moderate";
  compensation = "Free shipping on next order";
} else {
  severity = "minor";
  compensation = "Apology with tracking update";
}

return {
  delay_days: delayDays,
  severity: severity,
  compensation: compensation,
  compensation_value: compensationValue,
  tracking_url: "https://track.aftership.com/" + (vars.tracking_number || "")
};`,
          outputs: [
            { name: "delay_days", type: "number" },
            { name: "severity", type: "string" },
            { name: "compensation", type: "string" },
            { name: "compensation_value", type: "number" },
            { name: "tracking_url", type: "string" },
          ],
        },
      }),
      n("5", "condition", "Delay > 5 Days?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "delay_days", operator: "greaterThan", value: "5" },
          ],
        },
      }),
      emailNode(
        "6",
        "Send Proactive Delay Notification",
        1240,
        "{customer_email}",
        "Update on your order #{order_number}",
        "Hi {customer_first_name},\\n\\nWe wanted to let you know about a delay with your order #{order_number} ({item_name}).\\n\\nOriginal delivery estimate: {original_eta}\\nUpdated delivery estimate: {new_eta}\\nDelay reason: {delay_reason}\\nCarrier: {carrier}\\n\\nTrack your package: {tracking_url}\\n\\nAs a thank you for your patience, we are providing: {compensation}\\n\\nWe sincerely apologize for the inconvenience. If you have any questions, reply to this email or call us at 1-800-555-0199.\\n\\nBest,\\nFulfillment Team",
      ),
      smsNode(
        "7",
        "Urgent SMS Delay Alert",
        1240,
        "{customer_phone}",
        "Hi {customer_first_name}, your order #{order_number} is delayed by {delay_days} days. We are sorry for the inconvenience. Track: {tracking_url} — Call us: 1-800-555-0199 for immediate help.",
        400,
      ),
      slackNode(
        "8",
        "Alert Fulfillment Team",
        1480,
        ":package: *Delivery Delay Alert ({severity})*\\n• Order: #{order_number} — {item_name}\\n• Customer: {customer_first_name} {customer_last_name} ({customer_email})\\n• Carrier: {carrier} — Tracking: {tracking_number}\\n• Delay: {delay_days} days ({delay_reason})\\n• Compensation: {compensation}\\n• Order Total: ${order_total}",
      ),
      sheetsNode(
        "9",
        "Log Delay Recovery Metrics",
        1720,
        "DelayRecovery",
        '[["{order_number}","{customer_email}","{carrier}","{delay_days}","{severity}","{delay_reason}","{compensation}","{compensation_value}","{order_total}","{todays_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_1"),
      e("5", "6", "output_2"),
      e("6", "8"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ─── S5 LOGISTICS (7): Exchange Initiated Support ───
  "Exchange Initiated Support": {
    summary:
      "When a customer initiates an exchange, fetches order and item details from Shopify, checks exchange eligibility against return policy, verifies replacement item inventory, sends exchange confirmation with prepaid return label if available, creates the exchange order in Shopify, and logs exchange reasons for analysis.",
    nodes: [
      trigger("Customer Initiates Exchange", "webhook_app", {
        source: "shopify",
        event: "returns/request",
        howItStarts:
          "Fires when a customer submits an exchange request through the storefront or support portal",
        setup: "Configure Shopify webhook for return/exchange request events",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Extract Exchange Request Details",
        280,
        "You are a returns data extraction assistant. Parse the Shopify webhook payload to find the order ID, customer email, customer name, original item SKU, original item name, requested replacement item SKU, requested replacement item name, exchange reason, and order date. Return JSON regardless of payload format.",
        "Extract exchange request details from this webhook: {webhook_payload}",
        [
          { field: "order_id", as: "order_id", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "original_sku", as: "original_sku", type: "string" },
          {
            field: "original_item_name",
            as: "original_item_name",
            type: "string",
          },
          { field: "replacement_sku", as: "replacement_sku", type: "string" },
          {
            field: "replacement_item_name",
            as: "replacement_item_name",
            type: "string",
          },
          { field: "exchange_reason", as: "exchange_reason", type: "string" },
          { field: "order_date", as: "order_date", type: "string" },
        ],
      ),
      n("3", "hitApi", "Fetch Order and Item Details from Shopify", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/orders/{order_id}.json?fields=id,name,created_at,line_items,total_price,fulfillment_status",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "order.name", customName: "order_number", type: "string" },
            {
              name: "order.created_at",
              customName: "order_created",
              type: "string",
            },
            {
              name: "order.total_price",
              customName: "order_total",
              type: "string",
            },
            {
              name: "order.fulfillment_status",
              customName: "fulfillment_status",
              type: "string",
            },
          ],
        },
      }),
      n("4", "runCode", "Check Exchange Eligibility", 760, {
        codeDetails: {
          language: "javascript",
          code: `// Check exchange eligibility against return policy
const orderDate = new Date(vars.order_created);
const now = new Date();
const daysSincePurchase = Math.ceil((now - orderDate) / (1000 * 60 * 60 * 24));
const exchangeWindowDays = 30;
const finalSalePrefixes = ["FINAL-", "CLEARANCE-", "AS-IS-"];

const isFinalSale = finalSalePrefixes.some(p => (vars.original_sku || "").startsWith(p));
const withinWindow = daysSincePurchase <= exchangeWindowDays;
const isDelivered = vars.fulfillment_status === "fulfilled";

let eligible = withinWindow && !isFinalSale && isDelivered;
let reason = "";

if (!withinWindow) reason = "Exchange window of " + exchangeWindowDays + " days has passed (" + daysSincePurchase + " days since purchase)";
else if (isFinalSale) reason = "Item is marked as final sale and cannot be exchanged";
else if (!isDelivered) reason = "Order has not been delivered yet — exchange not available until delivery is confirmed";
else reason = "Eligible for exchange";

return { exchange_eligible: eligible, eligibility_reason: reason, days_since_purchase: daysSincePurchase };`,
          outputs: [
            { name: "exchange_eligible", type: "boolean" },
            { name: "eligibility_reason", type: "string" },
            { name: "days_since_purchase", type: "number" },
          ],
        },
      }),
      n("5", "hitApi", "Check Replacement Inventory in Shopify", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/variants.json?sku={replacement_sku}&fields=id,sku,title,inventory_quantity,price",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "variants[0].inventory_quantity",
              customName: "replacement_stock",
              type: "number",
            },
            {
              name: "variants[0].price",
              customName: "replacement_price",
              type: "string",
            },
            {
              name: "variants[0].id",
              customName: "replacement_variant_id",
              type: "string",
            },
          ],
        },
      }),
      n("6", "condition", "Replacement Item Available?", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "replacement_stock",
              operator: "greaterThan",
              value: "0",
            },
          ],
        },
      }),
      emailNode(
        "7",
        "Send Exchange Confirmation with Return Label",
        1480,
        "{customer_email}",
        "Exchange confirmed for order {order_number}",
        "Hi {customer_name},\\n\\nGreat news! Your exchange has been approved.\\n\\nOriginal item: {original_item_name} (SKU: {original_sku})\\nReplacement: {replacement_item_name} (SKU: {replacement_sku})\\n\\nHere is what to do next:\\n1. Pack your original item in its original packaging\\n2. Use the prepaid return label attached to this email\\n3. Drop it off at your nearest carrier location\\n4. Once we receive your return, your replacement will ship within 1-2 business days\\n\\nTracking for your return will be provided once scanned.\\n\\nThank you for your patience!\\nSupport Team",
      ),
      n("8", "hitApi", "Create Exchange Order in Shopify", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/orders.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "order",
              value:
                '{"email":"{customer_email}","line_items":[{"variant_id":"{replacement_variant_id}","quantity":1}],"tags":"exchange,original-order-{order_id}","note":"Exchange for order {order_number} — Reason: {exchange_reason}","financial_status":"paid"}',
            },
          ],
          variables: [
            {
              name: "order.id",
              customName: "exchange_order_id",
              type: "string",
            },
            {
              name: "order.name",
              customName: "exchange_order_number",
              type: "string",
            },
          ],
        },
      }),
      sheetsNode(
        "9",
        "Log Exchange Reasons and Completion",
        1960,
        "ExchangeLog",
        '[["{order_number}","{customer_email}","{original_item_name}","{replacement_item_name}","{exchange_reason}","{days_since_purchase}","{exchange_eligible}","{replacement_stock}","{todays_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "9", "output_2"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ─── S5 LOGISTICS (8): Failed Delivery Support Rescue ───
  "Failed Delivery Support Rescue": {
    summary:
      "When a carrier reports a delivery failure, AI extracts the failure reason, fetches order and shipping details from ShipStation, determines the rescue path (reschedule, reroute, or hold), checks if it is an address issue, notifies the customer with resolution options via email and SMS, and logs rescue outcomes.",
    nodes: [
      trigger("Carrier Reports Delivery Failure", "webhook_app", {
        source: "shipstation",
        event: "DELIVERY_EXCEPTION",
        howItStarts:
          "Fires when a carrier reports a failed delivery attempt or delivery exception",
        setup: "Configure ShipStation webhook for delivery exception events",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Extract Failure Reason from Carrier",
        280,
        "You are a shipping data extraction assistant. Parse the carrier delivery failure webhook to find the order number, tracking number, carrier name, delivery attempt date, failure reason (address_issue, no_one_home, refused, damaged, weather, access_issue), delivery address, and customer email. Return JSON regardless of payload format.",
        "Extract delivery failure details from this carrier webhook: {webhook_payload}",
        [
          { field: "order_number", as: "order_number", type: "string" },
          { field: "tracking_number", as: "tracking_number", type: "string" },
          { field: "carrier", as: "carrier", type: "string" },
          { field: "attempt_date", as: "attempt_date", type: "string" },
          { field: "failure_reason", as: "failure_reason", type: "string" },
          { field: "delivery_address", as: "delivery_address", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
        ],
      ),
      n("3", "hitApi", "Fetch Order and Shipping Details", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://ssapi.shipstation.com/orders?orderNumber={order_number}",
          headers: [
            { key: "Authorization", value: "Basic {shipstation_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "orders[0].orderId",
              customName: "shipstation_order_id",
              type: "string",
            },
            {
              name: "orders[0].shipTo.name",
              customName: "customer_name",
              type: "string",
            },
            {
              name: "orders[0].shipTo.phone",
              customName: "customer_phone",
              type: "string",
            },
            {
              name: "orders[0].items[0].name",
              customName: "item_name",
              type: "string",
            },
            {
              name: "orders[0].orderTotal",
              customName: "order_total",
              type: "number",
            },
          ],
        },
      }),
      n("4", "runCode", "Determine Rescue Path by Failure Reason", 760, {
        codeDetails: {
          language: "javascript",
          code: `// Determine rescue action based on failure reason
const reason = (vars.failure_reason || "").toLowerCase();
let rescuePath = "reschedule";
let customerAction = "";
let internalAction = "";
let isAddressIssue = false;

if (reason.includes("address") || reason.includes("wrong") || reason.includes("invalid")) {
  rescuePath = "reroute";
  isAddressIssue = true;
  customerAction = "Please confirm or update your delivery address so we can reroute your package.";
  internalAction = "Hold shipment — awaiting customer address confirmation";
} else if (reason.includes("no_one_home") || reason.includes("not home") || reason.includes("absent")) {
  rescuePath = "reschedule";
  customerAction = "We missed you! Choose a new delivery time or authorize safe drop-off.";
  internalAction = "Schedule re-delivery attempt for next business day";
} else if (reason.includes("refused")) {
  rescuePath = "hold";
  customerAction = "Your package was marked as refused. Please contact us if this was an error.";
  internalAction = "Hold at facility — contact customer to confirm intent";
} else if (reason.includes("damaged")) {
  rescuePath = "reship";
  customerAction = "Your package was damaged in transit. We are sending a replacement right away.";
  internalAction = "File carrier claim and create replacement shipment";
} else {
  customerAction = "We were unable to deliver your package. Please choose how you would like to proceed.";
  internalAction = "Review delivery exception and determine next steps";
}

return {
  rescue_path: rescuePath,
  is_address_issue: isAddressIssue,
  customer_action: customerAction,
  internal_action: internalAction,
  tracking_url: "https://track.aftership.com/" + (vars.tracking_number || "")
};`,
          outputs: [
            { name: "rescue_path", type: "string" },
            { name: "is_address_issue", type: "boolean" },
            { name: "customer_action", type: "string" },
            { name: "internal_action", type: "string" },
            { name: "tracking_url", type: "string" },
          ],
        },
      }),
      n("5", "condition", "Customer Address Issue?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "is_address_issue", operator: "equals", value: "true" },
          ],
        },
      }),
      emailNode(
        "6",
        "Notify Customer with Resolution Options",
        1240,
        "{customer_email}",
        "Delivery issue with your order #{order_number}",
        "Hi {customer_name},\\n\\nWe attempted to deliver your order #{order_number} ({item_name}) on {attempt_date}, but ran into an issue:\\n\\nReason: {failure_reason}\\n\\n{customer_action}\\n\\nTrack your package: {tracking_url}\\n\\nTo resolve this quickly, please reply to this email or click below:\\n• [Update Delivery Address]\\n• [Reschedule Delivery]\\n• [Hold at Facility for Pickup]\\n\\nWe want to make sure you get your order as soon as possible.\\n\\nBest,\\nDelivery Support Team",
      ),
      smsNode(
        "7",
        "SMS Delivery Rescue Link",
        1480,
        "{customer_phone}",
        "Hi {customer_name}, your order #{order_number} could not be delivered ({failure_reason}). {customer_action} Track: {tracking_url} — Reply HELP for support.",
      ),
      sheetsNode(
        "8",
        "Log Rescue Outcomes",
        1720,
        "DeliveryRescue",
        '[["{order_number}","{customer_email}","{carrier}","{tracking_number}","{failure_reason}","{rescue_path}","{is_address_issue}","{order_total}","{todays_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ─── S5 LOGISTICS (9): Reservation Confirmation ───
  "Reservation Confirmation": {
    summary:
      "When a reservation is created, compiles reservation details including date, time, party size, and venue, sends a confirmation email with directions and calendar attachment, then 24 hours before the reservation sends a reminder with running-late options. Large parties (>6) trigger a Slack notification to venue staff for special requirements.",
    nodes: [
      trigger("Reservation Created Webhook", "webhook_app", {
        source: "opentable",
        event: "reservation.created",
        howItStarts:
          "Fires when a new reservation is created in the booking system",
        setup:
          "Connect your reservation system (OpenTable, Resy, or custom) webhook for new reservation events",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Extract Reservation Details",
        280,
        "You are a reservation data extraction assistant. Parse the webhook payload to find the customer name, customer email, customer phone, reservation date, reservation time, party size, venue name, venue address, special requests, and confirmation number. Return JSON regardless of payload format.",
        "Extract reservation details from this webhook: {webhook_payload}",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_phone", as: "customer_phone", type: "string" },
          { field: "reservation_date", as: "reservation_date", type: "string" },
          { field: "reservation_time", as: "reservation_time", type: "string" },
          { field: "party_size", as: "party_size", type: "number" },
          { field: "venue_name", as: "venue_name", type: "string" },
          { field: "venue_address", as: "venue_address", type: "string" },
          { field: "special_requests", as: "special_requests", type: "string" },
          {
            field: "confirmation_number",
            as: "confirmation_number",
            type: "string",
          },
        ],
      ),
      emailNode(
        "3",
        "Send Reservation Confirmation",
        520,
        "{customer_email}",
        "Reservation confirmed at {venue_name} — #{confirmation_number}",
        "Hi {customer_name},\\n\\nYour reservation is confirmed!\\n\\nDetails:\\n• Venue: {venue_name}\\n• Address: {venue_address}\\n• Date: {reservation_date}\\n• Time: {reservation_time}\\n• Party Size: {party_size}\\n• Confirmation #: {confirmation_number}\\n• Special Requests: {special_requests}\\n\\nDirections: https://maps.google.com/?q={venue_address}\\n\\nNeed to modify or cancel? Reply to this email or call us.\\n\\nSee you soon!",
      ),
      n("4", "delayAction", "Wait Until 24 Hours Before Reservation", 760, {
        delayDetails: { duration: 24, unit: "hours" },
      }),
      emailNode(
        "5",
        "Send Reservation Reminder",
        1000,
        "{customer_email}",
        "Reminder: Your reservation at {venue_name} tomorrow at {reservation_time}",
        "Hi {customer_name},\\n\\nJust a reminder about your upcoming reservation:\\n\\n• Venue: {venue_name}\\n• Time: {reservation_time}\\n• Party Size: {party_size}\\n• Address: {venue_address}\\n\\nDirections: https://maps.google.com/?q={venue_address}\\n\\nRunning late? Let us know:\\n• [I will be 15 min late] — we will hold your table\\n• [I need to reschedule]\\n• [Cancel reservation]\\n\\nLooking forward to seeing you!",
      ),
      n("6", "condition", "Large Party (> 6)?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "party_size", operator: "greaterThan", value: "6" },
          ],
        },
      }),
      slackNode(
        "7",
        "Notify Venue Staff of Large Party",
        1480,
        ":fork_and_knife: *Large Party Alert*\\n• Guest: {customer_name} — Party of {party_size}\\n• Date: {reservation_date} at {reservation_time}\\n• Confirmation: #{confirmation_number}\\n• Special Requests: {special_requests}\\nPlease prepare seating arrangement and brief the floor team.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
    ],
  },

  // ─── S5 LOGISTICS (10): Reservation Reminder ───
  "Reservation Reminder": {
    summary:
      "Runs daily at 8 AM to fetch tomorrow's reservations, identifies those needing reminders, sends email reminders with reschedule links, sends same-day SMS reminders 4 hours before, and flags no-show risks for staff via Slack when no confirmation is received.",
    nodes: [
      trigger("Daily Reservation Reminder", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts:
          "Runs daily at 8 AM to process reminder notifications for tomorrow's reservations",
        setup: "No external setup needed — runs on schedule",
        inputFields: [],
      }),
      n("2", "hitApi", "Fetch Tomorrows Reservations", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.opentable.com/v1/reservations?date={tomorrows_date}&status=confirmed",
          headers: [
            { key: "Authorization", value: "Bearer {opentable_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "reservations",
              customName: "tomorrows_reservations",
              type: "object",
            },
            { name: "total", customName: "total_reservations", type: "number" },
          ],
        },
      }),
      n("3", "runCode", "Identify Reservations Needing Reminders", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Process reservation list and extract details for reminder
const reservations = vars.tomorrows_reservations || [];
const res = reservations[0] || {};

return {
  guest_name: res.guest_name || res.customer_name || "Guest",
  guest_email: res.guest_email || res.customer_email || "",
  guest_phone: res.guest_phone || res.customer_phone || "",
  reservation_time: res.time || res.reservation_time || "7:00 PM",
  party_size: res.party_size || res.covers || 2,
  venue_name: res.venue_name || res.restaurant_name || "Our Restaurant",
  venue_address: res.venue_address || res.address || "",
  confirmation_number: res.confirmation_number || res.id || "",
  confirmed: res.confirmed || false,
  total_needing_reminder: reservations.filter(r => !r.confirmed).length
};`,
          outputs: [
            { name: "guest_name", type: "string" },
            { name: "guest_email", type: "string" },
            { name: "guest_phone", type: "string" },
            { name: "reservation_time", type: "string" },
            { name: "party_size", type: "number" },
            { name: "venue_name", type: "string" },
            { name: "venue_address", type: "string" },
            { name: "confirmation_number", type: "string" },
            { name: "confirmed", type: "boolean" },
            { name: "total_needing_reminder", type: "number" },
          ],
        },
      }),
      emailNode(
        "4",
        "Send Reservation Reminder Email",
        760,
        "{guest_email}",
        "Reminder: {venue_name} tomorrow at {reservation_time}",
        "Hi {guest_name},\\n\\nThis is a friendly reminder about your reservation:\\n\\n• Venue: {venue_name}\\n• Date: Tomorrow\\n• Time: {reservation_time}\\n• Party Size: {party_size}\\n• Confirmation: #{confirmation_number}\\n• Address: {venue_address}\\n\\nGet Directions: https://maps.google.com/?q={venue_address}\\n\\nNeed to make changes?\\n• [Confirm Reservation]\\n• [Reschedule]\\n• [Cancel]\\n\\nWe look forward to seeing you!",
      ),
      smsNode(
        "5",
        "Same-Day SMS Reminder",
        1000,
        "{guest_phone}",
        "Hi {guest_name}! Reminder: your reservation at {venue_name} is today at {reservation_time} for {party_size} guests. Confirmation #{confirmation_number}. Reply C to confirm or R to reschedule.",
      ),
      n("6", "condition", "No Confirmation Received?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "confirmed", operator: "equals", value: "false" },
          ],
        },
      }),
      slackNode(
        "7",
        "Flag No-Show Risks for Staff",
        1480,
        ":warning: *Potential No-Show Alert*\\n• Guest: {guest_name} ({guest_email})\\n• Reservation: Tomorrow at {reservation_time} — Party of {party_size}\\n• Confirmation #{confirmation_number}\\n• Status: No confirmation received despite reminders\\n• Total unconfirmed for tomorrow: {total_needing_reminder}\\nConsider calling to confirm.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
    ],
  },

  // ─── S5 LOGISTICS (11): Return Initiated Confirmation ───
  "Return Initiated Confirmation": {
    summary:
      "When a return request is submitted, fetches order details and return policy from Shopify, validates return eligibility (within window, item condition, non-final-sale), sends confirmation with prepaid label for eligible returns or explains policy with alternatives for ineligible ones, and logs return reasons.",
    nodes: [
      trigger("Return Request Submitted", "webhook_app", {
        source: "shopify",
        event: "returns/request",
        howItStarts:
          "Fires when a customer submits a return request through the store or support portal",
        setup: "Configure Shopify webhook for return request events",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Extract Return Request Details",
        280,
        "You are a returns data extraction assistant. Parse the webhook to find the order ID, customer email, customer name, item SKU, item name, return reason, item condition, and order date. Return JSON regardless of payload format.",
        "Extract return request details from this webhook: {webhook_payload}",
        [
          { field: "order_id", as: "order_id", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "item_sku", as: "item_sku", type: "string" },
          { field: "item_name", as: "item_name", type: "string" },
          { field: "return_reason", as: "return_reason", type: "string" },
          { field: "item_condition", as: "item_condition", type: "string" },
        ],
      ),
      n("3", "hitApi", "Fetch Order Details from Shopify", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/orders/{order_id}.json?fields=id,name,created_at,line_items,total_price,fulfillment_status,tags",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "order.name", customName: "order_number", type: "string" },
            {
              name: "order.created_at",
              customName: "order_created",
              type: "string",
            },
            {
              name: "order.total_price",
              customName: "order_total",
              type: "string",
            },
            { name: "order.tags", customName: "order_tags", type: "string" },
            {
              name: "order.fulfillment_status",
              customName: "fulfillment_status",
              type: "string",
            },
          ],
        },
      }),
      n("4", "runCode", "Validate Return Eligibility", 760, {
        codeDetails: {
          language: "javascript",
          code: `// Validate return against policy rules
const orderDate = new Date(vars.order_created);
const now = new Date();
const daysSincePurchase = Math.ceil((now - orderDate) / (1000 * 60 * 60 * 24));
const returnWindowDays = 30;
const tags = (vars.order_tags || "").toLowerCase();
const condition = (vars.item_condition || "").toLowerCase();
const sku = (vars.item_sku || "").toUpperCase();

const isFinalSale = tags.includes("final-sale") || sku.startsWith("FINAL-") || sku.startsWith("CLEARANCE-");
const withinWindow = daysSincePurchase <= returnWindowDays;
const acceptableCondition = ["new", "unopened", "like_new", "defective"].includes(condition);
const isDelivered = vars.fulfillment_status === "fulfilled";

let eligible = withinWindow && !isFinalSale && acceptableCondition && isDelivered;
let ineligibility_reason = "";

if (!withinWindow) ineligibility_reason = "Return window of " + returnWindowDays + " days has passed (" + daysSincePurchase + " days since purchase)";
else if (isFinalSale) ineligibility_reason = "This item was purchased as final sale and is not eligible for return";
else if (!acceptableCondition) ineligibility_reason = "Item condition (" + condition + ") does not meet our return policy requirements";
else if (!isDelivered) ineligibility_reason = "Order has not been delivered yet";

return {
  return_eligible: eligible,
  ineligibility_reason: ineligibility_reason,
  days_since_purchase: daysSincePurchase,
  return_window_days: returnWindowDays
};`,
          outputs: [
            { name: "return_eligible", type: "boolean" },
            { name: "ineligibility_reason", type: "string" },
            { name: "days_since_purchase", type: "number" },
            { name: "return_window_days", type: "number" },
          ],
        },
      }),
      n("5", "condition", "Eligible for Return?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "return_eligible", operator: "equals", value: "true" },
          ],
        },
      }),
      emailNode(
        "6",
        "Send Return Confirmation with Prepaid Label",
        1240,
        "{customer_email}",
        "Return approved for order {order_number}",
        "Hi {customer_name},\\n\\nYour return request has been approved!\\n\\nReturn Details:\\n• Order: {order_number}\\n• Item: {item_name} (SKU: {item_sku})\\n• Reason: {return_reason}\\n• Refund Amount: ${order_total}\\n\\nNext Steps:\\n1. Pack the item securely in its original packaging\\n2. Print and attach the prepaid return label (attached)\\n3. Drop off at your nearest carrier location\\n4. Refund will be processed within 3-5 business days after we receive the item\\n\\nTrack your return status in your account dashboard.\\n\\nThank you,\\nReturns Team",
        80,
      ),
      emailNode(
        "7",
        "Explain Policy with Alternatives",
        1240,
        "{customer_email}",
        "Update on your return request for order {order_number}",
        "Hi {customer_name},\\n\\nThank you for reaching out about returning your order {order_number} ({item_name}).\\n\\nUnfortunately, this return cannot be processed:\\n{ineligibility_reason}\\n\\nHere are some alternatives we can offer:\\n• Exchange for a different item or size\\n• Store credit for the full purchase amount\\n• Speak with a customer service specialist for other options\\n\\nReply to this email or call us at 1-800-555-0199 to explore these options.\\n\\nWe appreciate your understanding.\\nCustomer Care Team",
        400,
      ),
      sheetsNode(
        "8",
        "Log Return Reasons",
        1480,
        "ReturnLog",
        '[["{order_number}","{customer_email}","{item_name}","{item_sku}","{return_reason}","{item_condition}","{return_eligible}","{ineligibility_reason}","{days_since_purchase}","{todays_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ─── S5 LOGISTICS (12): Return Received Plus Refund Status ───
  "Return Received Plus Refund Status": {
    summary:
      "When a warehouse receives a returned item, fetches return and order details from Shopify, processes inspection results to determine refund amount (full, partial, or rejected), initiates the refund for approved returns, notifies the customer of the refund status with timeline, explains rejection with appeal option for failed inspections, and logs outcomes.",
    nodes: [
      trigger("Warehouse Receives Return", "webhook_app", {
        source: "shipstation",
        event: "RETURN_RECEIVED",
        howItStarts:
          "Fires when the warehouse scans and receives a returned package",
        setup:
          "Configure your warehouse management system to send a webhook when returns are received and inspected",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Extract Return Receipt Details",
        280,
        "You are a warehouse data extraction assistant. Parse the webhook payload to find the return ID, order number, customer email, item SKU, item name, inspection result (pass/partial/fail), inspection notes, return condition, and refund amount. Return JSON regardless of payload format.",
        "Extract return receipt and inspection details from this warehouse webhook: {webhook_payload}",
        [
          { field: "return_id", as: "return_id", type: "string" },
          { field: "order_number", as: "order_number", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "item_sku", as: "item_sku", type: "string" },
          { field: "item_name", as: "item_name", type: "string" },
          {
            field: "inspection_result",
            as: "inspection_result",
            type: "string",
          },
          { field: "inspection_notes", as: "inspection_notes", type: "string" },
          { field: "return_condition", as: "return_condition", type: "string" },
        ],
      ),
      n("3", "hitApi", "Fetch Return and Order from Shopify", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/orders.json?name={order_number}&fields=id,name,email,total_price,line_items,customer",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "orders[0].id",
              customName: "shopify_order_id",
              type: "string",
            },
            {
              name: "orders[0].total_price",
              customName: "order_total",
              type: "string",
            },
            {
              name: "orders[0].customer.first_name",
              customName: "customer_name",
              type: "string",
            },
            {
              name: "orders[0].line_items[0].price",
              customName: "item_price",
              type: "string",
            },
          ],
        },
      }),
      n("4", "runCode", "Process Inspection and Calculate Refund", 760, {
        codeDetails: {
          language: "javascript",
          code: `// Determine refund based on inspection results
const result = (vars.inspection_result || "").toLowerCase();
const itemPrice = parseFloat(vars.item_price) || 0;
let refundAmount = 0;
let refundType = "none";
let refundNote = "";

if (result === "pass" || result === "approved") {
  refundAmount = itemPrice;
  refundType = "full";
  refundNote = "Full refund — item passed inspection in acceptable condition";
} else if (result === "partial") {
  refundAmount = Math.round(itemPrice * 0.70 * 100) / 100;
  refundType = "partial";
  refundNote = "Partial refund (70%) — item shows signs of use: " + (vars.inspection_notes || "minor wear");
} else {
  refundAmount = 0;
  refundType = "rejected";
  refundNote = "Refund denied — " + (vars.inspection_notes || "item does not meet return condition requirements");
}

return {
  refund_amount: refundAmount,
  refund_type: refundType,
  refund_note: refundNote,
  inspection_passed: result === "pass" || result === "approved" || result === "partial"
};`,
          outputs: [
            { name: "refund_amount", type: "number" },
            { name: "refund_type", type: "string" },
            { name: "refund_note", type: "string" },
            { name: "inspection_passed", type: "boolean" },
          ],
        },
      }),
      n("5", "condition", "Inspection Passed?", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "inspection_passed",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
      n("6", "hitApi", "Initiate Refund in Shopify", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/orders/{shopify_order_id}/refunds.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "refund",
              value:
                '{"currency":"USD","notify":false,"note":"{refund_note}","shipping":{"full_refund":false},"transactions":[{"kind":"refund","amount":"{refund_amount}"}]}',
            },
          ],
          variables: [
            { name: "refund.id", customName: "refund_id", type: "string" },
            {
              name: "refund.created_at",
              customName: "refund_created",
              type: "string",
            },
          ],
        },
      }),
      emailNode(
        "7",
        "Notify Customer of Refund Status",
        1480,
        "{customer_email}",
        "Refund update for order {order_number}",
        "Hi {customer_name},\\n\\nWe have received and inspected your returned item ({item_name}).\\n\\nRefund Status: {refund_type} refund\\nRefund Amount: ${refund_amount}\\nDetails: {refund_note}\\n\\nYour refund has been initiated and will appear in your original payment method within 5-10 business days.\\n\\nRefund Reference: {refund_id}\\n\\nIf you have any questions about your refund, reply to this email.\\n\\nThank you,\\nReturns Team",
      ),
      emailNode(
        "8",
        "Explain Rejection with Appeal Option",
        1480,
        "{customer_email}",
        "Update on your return for order {order_number}",
        "Hi {customer_name},\\n\\nWe have received and inspected your returned item ({item_name}).\\n\\nUnfortunately, we are unable to process a refund:\\n{refund_note}\\n\\nInspection Notes: {inspection_notes}\\nItem Condition: {return_condition}\\n\\nIf you believe this decision is incorrect, you can appeal within 14 days:\\n• Reply to this email with any additional context or photos\\n• A senior returns specialist will review your case within 2 business days\\n\\nWe appreciate your understanding.\\nReturns Team",
        400,
      ),
      sheetsNode(
        "9",
        "Log Inspection and Refund Outcomes",
        1720,
        "RefundLog",
        '[["{order_number}","{return_id}","{customer_email}","{item_name}","{inspection_result}","{return_condition}","{refund_type}","{refund_amount}","{refund_note}","{todays_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "9"),
      e("8", "9"),
    ],
  },

  // ─── S5 LOGISTICS (13): Return Support Recovery ───
  "Return Support Recovery": {
    summary:
      "When a return is initiated, fetches order and customer history from Shopify, uses AI to analyze the return reason and suggest save-the-sale alternatives (exchange, store credit, discount), presents alternatives to the customer, waits 48 hours, then checks if the customer chose an alternative and tracks recovery rates.",
    nodes: [
      trigger("Return Initiated Webhook", "webhook_app", {
        source: "shopify",
        event: "returns/request",
        howItStarts:
          "Fires when a customer initiates a return through the store or support portal",
        setup: "Configure Shopify webhook for return request events",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Extract Return Details",
        280,
        "You are a returns data extraction assistant. Parse the webhook to find the order ID, order number, customer email, customer name, item name, item SKU, return reason, item price, and any customer comments. Return JSON regardless of payload format.",
        "Extract return details from this webhook: {webhook_payload}",
        [
          { field: "order_id", as: "order_id", type: "string" },
          { field: "order_number", as: "order_number", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "item_name", as: "item_name", type: "string" },
          { field: "item_sku", as: "item_sku", type: "string" },
          { field: "return_reason", as: "return_reason", type: "string" },
          { field: "item_price", as: "item_price", type: "string" },
        ],
      ),
      n("3", "hitApi", "Fetch Order and Customer History from Shopify", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/orders/{order_id}.json?fields=id,name,total_price,line_items,customer",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "order.customer.orders_count",
              customName: "customer_order_count",
              type: "number",
            },
            {
              name: "order.customer.total_spent",
              customName: "customer_total_spent",
              type: "string",
            },
            {
              name: "order.total_price",
              customName: "order_total",
              type: "string",
            },
          ],
        },
      }),
      aiNode(
        "4",
        "AI Suggest Save-the-Sale Alternatives",
        760,
        "You are a customer retention specialist for e-commerce. Analyze the return reason and suggest save-the-sale alternatives. Consider: exchange for different size/color, store credit with bonus (e.g., 110% value), discount on next purchase, or a different product recommendation. Return JSON with has_alternative (boolean), alternative_type, alternative_description, and alternative_value. Be creative but realistic.",
        "Return reason: {return_reason}. Item: {item_name} (${item_price}). Customer orders: {customer_order_count}, total spent: ${customer_total_spent}. Suggest save-the-sale alternatives.",
        [
          { field: "has_alternative", as: "has_alternative", type: "boolean" },
          { field: "alternative_type", as: "alternative_type", type: "string" },
          {
            field: "alternative_description",
            as: "alternative_description",
            type: "string",
          },
          {
            field: "alternative_value",
            as: "alternative_value",
            type: "string",
          },
        ],
      ),
      n("5", "condition", "Save-the-Sale Option Available?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "has_alternative", operator: "equals", value: "true" },
          ],
        },
      }),
      emailNode(
        "6",
        "Present Exchange, Credit, or Discount Alternative",
        1240,
        "{customer_email}",
        "Before you return — we have options for you, {customer_name}",
        "Hi {customer_name},\\n\\nWe received your return request for {item_name} from order {order_number}.\\n\\nBefore we process the return, we wanted to share some alternatives that might work better for you:\\n\\n{alternative_description}\\n\\nAlternative: {alternative_type} — Value: {alternative_value}\\n\\nChoose an option:\\n• [Accept Alternative] — we will set this up right away\\n• [Proceed with Return] — no problem, we will send your return label\\n\\nEither way, we want you to be happy with your experience.\\n\\nBest,\\nCustomer Care Team",
      ),
      n("7", "delayAction", "Wait 48 Hours for Customer Decision", 1480, {
        delayDetails: { duration: 48, unit: "hours" },
      }),
      n("8", "condition", "Customer Chose Alternative?", 1720, {
        conditionDetails: {
          conditions: [
            {
              variable: "alternative_accepted",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
      sheetsNode(
        "9",
        "Track Return Recovery Rate",
        1960,
        "ReturnRecovery",
        '[["{order_number}","{customer_email}","{item_name}","{return_reason}","{has_alternative}","{alternative_type}","{alternative_value}","{alternative_accepted}","{todays_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "9", "output_2"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9", "output_1"),
      e("8", "9", "output_2"),
    ],
  },

  // ─── S5 LOGISTICS (14): Service Appointment Follow-Up Logistics ───
  "Service Appointment Follow-Up Logistics": {
    summary:
      "When a service appointment is completed, compiles appointment details and service outcome, sends a service summary with maintenance tips, waits 4 hours then sends a satisfaction survey, and if a follow-up service is needed, suggests the next appointment with a Calendly booking link.",
    nodes: [
      trigger("Service Appointment Completed", "webhook_app", {
        source: "calendly",
        event: "invitee.completed",
        howItStarts:
          "Fires when a service appointment is marked as completed in the scheduling system",
        setup:
          "Configure your service management or scheduling system to fire a webhook when appointments are completed",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Extract Appointment and Service Outcome",
        280,
        "You are a service data extraction assistant. Parse the webhook payload to find the customer name, customer email, service type, appointment date, appointment time, technician/provider name, service outcome (completed/partial/requires_follow_up), service notes, and any follow-up recommendations. Return JSON regardless of payload format.",
        "Extract service appointment completion details from this webhook: {webhook_payload}",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "service_type", as: "service_type", type: "string" },
          { field: "appointment_date", as: "appointment_date", type: "string" },
          { field: "provider_name", as: "provider_name", type: "string" },
          { field: "service_outcome", as: "service_outcome", type: "string" },
          { field: "service_notes", as: "service_notes", type: "string" },
          {
            field: "follow_up_needed",
            as: "follow_up_needed",
            type: "boolean",
          },
          {
            field: "follow_up_recommendation",
            as: "follow_up_recommendation",
            type: "string",
          },
        ],
      ),
      emailNode(
        "3",
        "Send Service Summary with Maintenance Tips",
        520,
        "{customer_email}",
        "Service summary for your {service_type} appointment",
        "Hi {customer_name},\\n\\nThank you for your {service_type} appointment on {appointment_date} with {provider_name}.\\n\\nService Summary:\\n• Service: {service_type}\\n• Outcome: {service_outcome}\\n• Notes: {service_notes}\\n\\nMaintenance Tips:\\n• Schedule regular check-ups every 6 months\\n• Monitor for any changes and contact us if issues arise\\n• Keep this summary for your records\\n\\nIf you have any questions about the service performed, do not hesitate to reach out.\\n\\nBest,\\n{provider_name}",
      ),
      n("4", "delayAction", "Wait 4 Hours", 760, {
        delayDetails: { duration: 4, unit: "hours" },
      }),
      emailNode(
        "5",
        "Send Satisfaction Survey",
        1000,
        "{customer_email}",
        "How was your {service_type} experience?",
        "Hi {customer_name},\\n\\nWe hope your {service_type} appointment went well!\\n\\nWe would love your feedback to help us improve:\\n\\nRate your experience (click one):\\n[Excellent] [Good] [Fair] [Poor]\\n\\nQuick questions:\\n• Was your appointment on time?\\n• Was the service explained clearly?\\n• Would you recommend us to others?\\n\\n[Take 2-Minute Survey →]\\n\\nYour feedback helps us provide better service to you and all our customers.\\n\\nThank you,\\nCustomer Experience Team",
      ),
      n("6", "condition", "Follow-Up Service Needed?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "follow_up_needed", operator: "equals", value: "true" },
          ],
        },
      }),
      n("7", "hitApi", "Suggest Next Appointment via Calendly", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.calendly.com/scheduling_links",
          headers: [
            { key: "Authorization", value: "Bearer {calendly_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "max_event_count", value: "1" },
            {
              key: "owner",
              value:
                "https://api.calendly.com/event_types/{service_event_type_uuid}",
            },
            { key: "owner_type", value: "EventType" },
          ],
          variables: [
            {
              name: "resource.booking_url",
              customName: "booking_url",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
    ],
  },

  // ─── S5 LOGISTICS (15): Store Appointment Reminder ───
  "Store Appointment Reminder": {
    summary:
      "Runs daily at 7 AM to fetch today's store appointments, compiles details with location help and prep info, sends email reminders with parking info, sends 1-hour-before SMS reminders, and flags possible no-shows via Slack when appointments are within 30 minutes with no check-in.",
    nodes: [
      trigger("Daily Store Appointment Check", "schedule", {
        schedule: "Every day at 7:00 AM",
        howItStarts:
          "Runs daily at 7 AM to process reminder notifications for today's store appointments",
        setup: "No external setup needed — runs on schedule",
        inputFields: [],
      }),
      n("2", "hitApi", "Fetch Todays Store Appointments", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.calendly.com/scheduled_events?min_start_time={today_start}&max_start_time={today_end}&status=active",
          headers: [
            { key: "Authorization", value: "Bearer {calendly_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "collection",
              customName: "todays_appointments",
              type: "object",
            },
            {
              name: "pagination.count",
              customName: "total_appointments",
              type: "number",
            },
          ],
        },
      }),
      n("3", "runCode", "Compile Appointment Details with Location Help", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Process appointments and compile details with directions
const appointments = vars.todays_appointments || [];
const apt = appointments[0] || {};

const startTime = apt.start_time || "";
const now = new Date();
const aptTime = new Date(startTime);
const minutesUntil = Math.round((aptTime - now) / (1000 * 60));

return {
  customer_name: apt.name || apt.invitees_counter?.active > 0 ? "Valued Customer" : "Guest",
  customer_email: apt.email || "",
  customer_phone: apt.phone || "",
  appointment_time: startTime ? new Date(startTime).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "Scheduled",
  appointment_type: apt.event_type || apt.name || "Store Appointment",
  store_name: "Main Street Store",
  store_address: "123 Main Street, Suite 100",
  parking_info: "Free parking available in the lot behind the building. Street parking also available on Main Street.",
  prep_info: "Please bring a valid photo ID and any relevant documents for your appointment.",
  minutes_until: minutesUntil,
  checked_in: false,
  is_imminent: minutesUntil <= 30 && minutesUntil > 0
};`,
          outputs: [
            { name: "customer_name", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "appointment_time", type: "string" },
            { name: "appointment_type", type: "string" },
            { name: "store_name", type: "string" },
            { name: "store_address", type: "string" },
            { name: "parking_info", type: "string" },
            { name: "prep_info", type: "string" },
            { name: "minutes_until", type: "number" },
            { name: "checked_in", type: "boolean" },
            { name: "is_imminent", type: "boolean" },
          ],
        },
      }),
      emailNode(
        "4",
        "Send Reminder with Store Directions",
        760,
        "{customer_email}",
        "Reminder: Your appointment at {store_name} today at {appointment_time}",
        "Hi {customer_name},\\n\\nThis is a reminder about your appointment today:\\n\\n• Type: {appointment_type}\\n• Time: {appointment_time}\\n• Location: {store_name}, {store_address}\\n\\nParking: {parking_info}\\n\\nDirections: https://maps.google.com/?q={store_address}\\n\\nPlease remember: {prep_info}\\n\\nNeed to reschedule? Reply to this email or call us.\\n\\nSee you today!",
      ),
      smsNode(
        "5",
        "1-Hour-Before SMS Reminder",
        1000,
        "{customer_phone}",
        "Hi {customer_name}! Your {appointment_type} at {store_name} is in about 1 hour ({appointment_time}). Address: {store_address}. Parking available behind the building. See you soon!",
      ),
      n("6", "condition", "Imminent Appointment with No Check-In?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "is_imminent", operator: "equals", value: "true" },
          ],
        },
      }),
      slackNode(
        "7",
        "Alert Store Staff — Possible No-Show",
        1480,
        ":clock1: *Possible No-Show Alert*\\n• Customer: {customer_name}\\n• Appointment: {appointment_type} at {appointment_time}\\n• Status: Within 30 minutes and no check-in detected\\n• Phone: {customer_phone}\\n• Email: {customer_email}\\nConsider calling to confirm they are on their way.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
    ],
  },

  // ─── S6 OUTAGE (16): Bug or Incident Communication ───
  "Bug or Incident Communication": {
    summary:
      "When a PagerDuty incident is created, AI extracts incident details and affected services, identifies affected customers by service mapping, checks for customer-facing impact, drafts clear incident communication, routes through incident commander approval, dispatches multi-channel notifications (email + Slack), and logs the communication timeline.",
    nodes: [
      trigger("PagerDuty Incident Created", "webhook_app", {
        source: "pagerduty",
        event: "incident.triggered",
        howItStarts: "Fires when a new incident is triggered in PagerDuty",
        setup:
          "Configure PagerDuty webhook subscription for incident.triggered events",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Extract Incident Details and Affected Services",
        280,
        "You are an incident management data extraction assistant. Parse the PagerDuty webhook payload to find the incident ID, incident title, incident description, severity level (critical/high/medium/low), affected service name, affected service ID, triggered at timestamp, and escalation policy. Return JSON regardless of payload format.",
        "Extract incident details from this PagerDuty webhook: {webhook_payload}",
        [
          { field: "incident_id", as: "incident_id", type: "string" },
          { field: "incident_title", as: "incident_title", type: "string" },
          {
            field: "incident_description",
            as: "incident_description",
            type: "string",
          },
          { field: "severity", as: "severity", type: "string" },
          { field: "affected_service", as: "affected_service", type: "string" },
          { field: "triggered_at", as: "triggered_at", type: "string" },
          {
            field: "escalation_policy",
            as: "escalation_policy",
            type: "string",
          },
        ],
      ),
      n("3", "runCode", "Identify Affected Customers by Service Mapping", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Map affected service to customer segments
const serviceMap = {
  "api-gateway": { segment: "all_api_users", count: 5000, customerFacing: true },
  "web-app": { segment: "web_users", count: 12000, customerFacing: true },
  "mobile-api": { segment: "mobile_users", count: 8000, customerFacing: true },
  "billing-service": { segment: "paying_customers", count: 3000, customerFacing: true },
  "analytics-pipeline": { segment: "analytics_users", count: 1500, customerFacing: false },
  "internal-tools": { segment: "internal_only", count: 0, customerFacing: false },
  "email-service": { segment: "email_users", count: 7000, customerFacing: true },
};

const service = (vars.affected_service || "").toLowerCase().replace(/\\s+/g, "-");
const mapping = serviceMap[service] || { segment: "unknown", count: 0, customerFacing: false };

return {
  affected_segment: mapping.segment,
  affected_count: mapping.count,
  is_customer_facing: mapping.customerFacing,
  impact_summary: mapping.customerFacing
    ? "Approximately " + mapping.count + " customers in the " + mapping.segment + " segment may be affected"
    : "Internal service — no direct customer impact expected"
};`,
          outputs: [
            { name: "affected_segment", type: "string" },
            { name: "affected_count", type: "number" },
            { name: "is_customer_facing", type: "boolean" },
            { name: "impact_summary", type: "string" },
          ],
        },
      }),
      n("4", "condition", "Customer-Facing Impact?", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "is_customer_facing",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
      aiNode(
        "5",
        "Draft Incident Communication",
        1000,
        "You are an incident communications specialist. Draft a clear, honest, and empathetic customer-facing incident notification. Include: what happened (without technical jargon), what services are affected, what we are doing about it, and expected resolution time. Be transparent but reassuring. Return JSON with email_subject, email_body, and status_page_update.",
        "Incident: {incident_title}. Description: {incident_description}. Severity: {severity}. Affected service: {affected_service}. Impact: {impact_summary}. Triggered at: {triggered_at}. Draft customer communication.",
        [
          {
            field: "email_subject",
            as: "incident_email_subject",
            type: "string",
          },
          { field: "email_body", as: "incident_email_body", type: "string" },
          {
            field: "status_page_update",
            as: "status_page_update",
            type: "string",
          },
        ],
      ),
      n("6", "approvalGate", "Incident Commander Approves Message", 1240, {
        approvalDetails: {
          approver: "incident_commander",
          timeout: 1,
          timeoutUnit: "hours",
        },
      }),
      emailNode(
        "7",
        "Dispatch Incident Notification",
        1480,
        "{affected_segment}@company.com",
        "{incident_email_subject}",
        "{incident_email_body}",
      ),
      slackNode(
        "8",
        "Post Internal Status Update",
        1480,
        ":rotating_light: *Incident Communication Sent*\\n• Incident: {incident_title} ({incident_id})\\n• Severity: {severity}\\n• Service: {affected_service}\\n• Impact: {impact_summary}\\n• Customer notification: Sent to {affected_count} customers\\n• Status page: Updated",
        400,
      ),
      sheetsNode(
        "9",
        "Log Communication Timeline",
        1720,
        "IncidentComms",
        '[["{incident_id}","{incident_title}","{severity}","{affected_service}","{affected_count}","{is_customer_facing}","notification_sent","{todays_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "9", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("6", "8"),
      e("7", "9"),
      e("8", "9"),
    ],
  },

  // ─── S6 OUTAGE (17): Outage Communication Bot ───
  "Outage Communication Bot": {
    summary:
      "When a customer asks about service status during an incident, checks the current incident status from StatusPage, uses AI to generate a contextual response, determines if the specific customer is affected, and replies with either incident status and ETA for affected customers or troubleshooting guidance for non-affected ones. Logs inquiry volume during incidents.",
    nodes: [
      trigger("Customer Asks About Service Status", "webhook_app", {
        source: "intercom",
        event: "conversation.user.created",
        howItStarts:
          "Fires when a customer sends a message asking about service status or outage during an active incident",
        setup:
          "Configure Intercom webhook for new conversation events with keyword triggers (outage, down, not working, status)",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Extract Customer Query Details",
        280,
        "You are a customer inquiry extraction assistant. Parse the webhook payload to find the customer email, customer name, conversation ID, customer message text, and the specific service or feature they are asking about. Return JSON regardless of payload format.",
        "Extract customer status inquiry details from this webhook: {webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "conversation_id", as: "conversation_id", type: "string" },
          { field: "customer_message", as: "customer_message", type: "string" },
          {
            field: "asked_about_service",
            as: "asked_about_service",
            type: "string",
          },
        ],
      ),
      n("3", "hitApi", "Check Current Incident Status from StatusPage", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.statuspage.io/v1/pages/{statuspage_id}/incidents/unresolved.json",
          headers: [
            { key: "Authorization", value: "OAuth {statuspage_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "[0].name",
              customName: "active_incident_name",
              type: "string",
            },
            {
              name: "[0].status",
              customName: "incident_status",
              type: "string",
            },
            {
              name: "[0].impact",
              customName: "incident_impact",
              type: "string",
            },
            {
              name: "[0].incident_updates[0].body",
              customName: "latest_update",
              type: "string",
            },
            {
              name: "[0].components[0].name",
              customName: "affected_component",
              type: "string",
            },
          ],
        },
      }),
      n("4", "runCode", "Determine if Customer is Affected", 760, {
        codeDetails: {
          language: "javascript",
          code: `// Check if the customer's question matches an active incident
const askedService = (vars.asked_about_service || "").toLowerCase();
const affectedComponent = (vars.affected_component || "").toLowerCase();
const incidentName = (vars.active_incident_name || "").toLowerCase();

const isAffected = affectedComponent.includes(askedService) ||
  incidentName.includes(askedService) ||
  askedService.includes(affectedComponent) ||
  (vars.active_incident_name && askedService === "");

const hasActiveIncident = !!vars.active_incident_name;

return {
  customer_affected: isAffected && hasActiveIncident,
  has_active_incident: hasActiveIncident,
  match_confidence: isAffected ? "high" : "low"
};`,
          outputs: [
            { name: "customer_affected", type: "boolean" },
            { name: "has_active_incident", type: "boolean" },
            { name: "match_confidence", type: "string" },
          ],
        },
      }),
      aiNode(
        "5",
        "Generate Contextual Response",
        1000,
        "You are a customer support bot responding during a service incident. Generate a helpful, empathetic response. If the customer is affected by an active incident, explain the situation, what we are doing, and provide an ETA. If they are not affected, provide troubleshooting steps. Return JSON with response_message and response_type (incident_update or troubleshooting).",
        "Customer question: {customer_message}. Service asked about: {asked_about_service}. Customer affected: {customer_affected}. Active incident: {active_incident_name}. Status: {incident_status}. Impact: {incident_impact}. Latest update: {latest_update}. Generate response.",
        [
          { field: "response_message", as: "response_message", type: "string" },
          { field: "response_type", as: "response_type", type: "string" },
        ],
      ),
      n("6", "condition", "Customer Affected by Incident?", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "customer_affected",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
      n("7", "sendMessage", "Reply with Incident Status and ETA", 1480, {
        sendMessageDetails: {
          channel: "intercom",
          conversationId: "{conversation_id}",
          message: "{response_message}",
        },
      }),
      n(
        "8",
        "sendMessage",
        "Reply with Troubleshooting Guidance",
        1480,
        {
          sendMessageDetails: {
            channel: "intercom",
            conversationId: "{conversation_id}",
            message: "{response_message}",
          },
        },
        400,
      ),
      sheetsNode(
        "9",
        "Log Inquiry Volume During Incidents",
        1720,
        "IncidentInquiries",
        '[["{customer_email}","{customer_name}","{customer_message}","{asked_about_service}","{customer_affected}","{active_incident_name}","{response_type}","{todays_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "9"),
      e("8", "9"),
    ],
  },

  // ─── S6 OUTAGE (18): Outage Notification Bot ───
  "Outage Notification Bot": {
    summary:
      "When an incident is declared in PagerDuty, AI analyzes scope and generates the initial notification, segments affected customers by service and tier, sends proactive email to affected customers, sends urgent SMS for major+ severity, posts internal Slack status updates, and tracks notification delivery and customer inquiries.",
    nodes: [
      trigger("PagerDuty Incident Declared", "webhook_app", {
        source: "pagerduty",
        event: "incident.triggered",
        howItStarts:
          "Fires when an incident is declared or triggered in PagerDuty",
        setup: "Configure PagerDuty webhook for incident.triggered events",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Analyze Incident Scope and Draft Notification",
        280,
        "You are an incident communication specialist. Analyze the PagerDuty incident payload and generate: 1) A clear scope assessment, 2) An initial customer notification email, 3) An SMS alert for critical situations. Use plain language, no technical jargon. Return JSON with incident_id, incident_title, severity (critical/major/minor), affected_services, scope_summary, email_subject, email_body, sms_message, and estimated_resolution.",
        "Analyze this incident and generate notifications: {webhook_payload}",
        [
          { field: "incident_id", as: "incident_id", type: "string" },
          { field: "incident_title", as: "incident_title", type: "string" },
          { field: "severity", as: "severity", type: "string" },
          {
            field: "affected_services",
            as: "affected_services",
            type: "string",
          },
          { field: "scope_summary", as: "scope_summary", type: "string" },
          {
            field: "email_subject",
            as: "outage_email_subject",
            type: "string",
          },
          { field: "email_body", as: "outage_email_body", type: "string" },
          { field: "sms_message", as: "outage_sms_message", type: "string" },
          {
            field: "estimated_resolution",
            as: "estimated_resolution",
            type: "string",
          },
        ],
      ),
      n("3", "runCode", "Segment Affected Customers by Service and Tier", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Segment customers by affected service and account tier
const services = (vars.affected_services || "").split(",").map(s => s.trim().toLowerCase());
const severity = (vars.severity || "").toLowerCase();

const isMajor = severity === "critical" || severity === "major";
const customerSegments = services.map(s => s.replace(/\\s+/g, "_") + "_users").join(",");

return {
  customer_segments: customerSegments,
  notification_email: customerSegments.replace(/_users/g, "") + "@notifications.company.com",
  sms_eligible: isMajor,
  is_major: isMajor,
  priority_label: isMajor ? "URGENT" : "INFO",
  sms_list: isMajor ? "premium_sms_list" : "none"
};`,
          outputs: [
            { name: "customer_segments", type: "string" },
            { name: "notification_email", type: "string" },
            { name: "sms_eligible", type: "boolean" },
            { name: "is_major", type: "boolean" },
            { name: "priority_label", type: "string" },
            { name: "sms_list", type: "string" },
          ],
        },
      }),
      n("4", "condition", "Severity >= Major?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "is_major", operator: "equals", value: "true" },
          ],
        },
      }),
      emailNode(
        "5",
        "Send Proactive Outage Notification",
        1000,
        "{notification_email}",
        "[{priority_label}] {outage_email_subject}",
        "{outage_email_body}",
      ),
      smsNode(
        "6",
        "Urgent SMS for Critical Services",
        1000,
        "{sms_list}",
        "{outage_sms_message}",
        400,
      ),
      slackNode(
        "7",
        "Post Internal Status Update",
        1240,
        ":rotating_light: *[{priority_label}] Incident Declared*\\n• ID: {incident_id}\\n• Title: {incident_title}\\n• Severity: {severity}\\n• Affected: {affected_services}\\n• Scope: {scope_summary}\\n• ETA: {estimated_resolution}\\n• Customer notification: Sent to segments: {customer_segments}\\n• SMS alerts: {sms_eligible}",
      ),
      sheetsNode(
        "8",
        "Track Notification Delivery",
        1480,
        "OutageNotifications",
        '[["{incident_id}","{incident_title}","{severity}","{affected_services}","{customer_segments}","{sms_eligible}","{estimated_resolution}","sent","{todays_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_1"),
      e("4", "5", "output_2"),
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ─── S6 OUTAGE (19): Outage-Resolved Follow-Up ───
  "Outage-Resolved Follow-Up": {
    summary:
      "When an incident is resolved in PagerDuty, AI compiles a postmortem summary from the incident timeline, calculates applicable service credits, sends a resolution notice with postmortem to affected customers, sends credit notifications where applicable, posts an internal resolution summary to Slack, and archives the incident with full metrics.",
    nodes: [
      trigger("PagerDuty Incident Resolved", "webhook_app", {
        source: "pagerduty",
        event: "incident.resolved",
        howItStarts:
          "Fires when an incident is marked as resolved in PagerDuty",
        setup: "Configure PagerDuty webhook for incident.resolved events",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Compile Postmortem Summary",
        280,
        "You are an incident postmortem specialist. Parse the PagerDuty resolution webhook to extract incident details and compile a customer-friendly postmortem summary. Include: what happened, root cause (in plain language), what we did to fix it, duration of impact, and what we are doing to prevent recurrence. Return JSON with incident_id, incident_title, severity, affected_services, duration_minutes, root_cause_summary, resolution_summary, prevention_measures, and customer_postmortem.",
        "Compile postmortem from this incident resolution webhook: {webhook_payload}",
        [
          { field: "incident_id", as: "incident_id", type: "string" },
          { field: "incident_title", as: "incident_title", type: "string" },
          { field: "severity", as: "severity", type: "string" },
          {
            field: "affected_services",
            as: "affected_services",
            type: "string",
          },
          { field: "duration_minutes", as: "duration_minutes", type: "number" },
          {
            field: "root_cause_summary",
            as: "root_cause_summary",
            type: "string",
          },
          {
            field: "resolution_summary",
            as: "resolution_summary",
            type: "string",
          },
          {
            field: "prevention_measures",
            as: "prevention_measures",
            type: "string",
          },
          {
            field: "customer_postmortem",
            as: "customer_postmortem",
            type: "string",
          },
        ],
      ),
      n("3", "runCode", "Calculate Service Credits", 760, {
        codeDetails: {
          language: "javascript",
          code: `// Calculate service credits based on SLA and outage duration
const duration = parseInt(vars.duration_minutes) || 0;
const severity = (vars.severity || "").toLowerCase();

let creditPercent = 0;
let creditsApplicable = false;

// SLA: 99.9% uptime = ~43 min/month allowed downtime
const monthlyAllowedMinutes = 43;

if (severity === "critical" && duration > 30) {
  creditPercent = 25;
  creditsApplicable = true;
} else if (severity === "critical" && duration > 10) {
  creditPercent = 10;
  creditsApplicable = true;
} else if (severity === "major" && duration > 60) {
  creditPercent = 15;
  creditsApplicable = true;
} else if (severity === "major" && duration > 30) {
  creditPercent = 5;
  creditsApplicable = true;
}

const durationHours = Math.round(duration / 60 * 10) / 10;

return {
  credits_applicable: creditsApplicable,
  credit_percent: creditPercent,
  credit_description: creditsApplicable
    ? creditPercent + "% service credit for " + durationHours + " hours of " + severity + " outage"
    : "No credits applicable — within SLA thresholds",
  duration_hours: durationHours,
  notification_email: (vars.affected_services || "").replace(/,\\s*/g, "_").toLowerCase() + "@notifications.company.com"
};`,
          outputs: [
            { name: "credits_applicable", type: "boolean" },
            { name: "credit_percent", type: "number" },
            { name: "credit_description", type: "string" },
            { name: "duration_hours", type: "number" },
            { name: "notification_email", type: "string" },
          ],
        },
      }),
      n("4", "condition", "Service Credits Applicable?", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "credits_applicable",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
      emailNode(
        "5",
        "Send Resolution Notice with Postmortem",
        1240,
        "{notification_email}",
        "Resolved: {incident_title}",
        "Hello,\\n\\nWe are writing to let you know that the incident affecting {affected_services} has been fully resolved.\\n\\n{customer_postmortem}\\n\\nDuration: {duration_hours} hours\\nRoot Cause: {root_cause_summary}\\nResolution: {resolution_summary}\\n\\nPrevention:\\n{prevention_measures}\\n\\nWe sincerely apologize for the disruption and are committed to improving our reliability. If you experienced any issues during this time, please reach out to our support team.\\n\\nThank you for your patience,\\nEngineering Team",
      ),
      emailNode(
        "6",
        "Send Service Credit Notification",
        1240,
        "{notification_email}",
        "Service credit applied: {incident_title}",
        "Hello,\\n\\nAs a result of the recent incident ({incident_title}) that lasted {duration_hours} hours, we have applied a service credit to your account.\\n\\nCredit Details:\\n{credit_description}\\n\\nThis credit will be reflected on your next invoice. No action is needed on your part.\\n\\nWe take our SLA commitments seriously and apologize for the inconvenience.\\n\\nThank you,\\nBilling Team",
        400,
      ),
      slackNode(
        "7",
        "Post Internal Resolution Summary",
        1480,
        ":white_check_mark: *Incident Resolved*\\n• ID: {incident_id} — {incident_title}\\n• Severity: {severity}\\n• Duration: {duration_hours} hours\\n• Services: {affected_services}\\n• Root Cause: {root_cause_summary}\\n• Resolution: {resolution_summary}\\n• Credits: {credit_description}\\n• Prevention: {prevention_measures}",
      ),
      sheetsNode(
        "8",
        "Archive Incident with Full Metrics",
        1720,
        "IncidentArchive",
        '[["{incident_id}","{incident_title}","{severity}","{affected_services}","{duration_hours}","{root_cause_summary}","{resolution_summary}","{credits_applicable}","{credit_percent}","{todays_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_1"),
      e("4", "5", "output_2"),
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ─── S6 OUTAGE (20): Proactive Service Alert ───
  "Proactive Service Alert": {
    summary:
      "When monitoring detects an anomaly before an official outage is declared, AI analyzes the signals and predicts potential impact, determines whether internal escalation or customer alert is needed, alerts engineering and support teams via Slack, sends early warnings to premium customers if customer impact is likely, and logs early detection effectiveness.",
    nodes: [
      trigger("Monitoring Anomaly Detected", "webhook_app", {
        source: "datadog",
        event: "monitor.alert",
        howItStarts:
          "Fires when Datadog or monitoring system detects an anomaly before it becomes a full outage",
        setup:
          "Configure Datadog webhook integration to forward monitor alert events",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Analyze Anomaly Signals and Predict Impact",
        280,
        "You are a site reliability analysis assistant. Parse the monitoring alert webhook and analyze the anomaly signals. Predict the potential customer impact, estimate severity, and assess likelihood of it becoming a full outage. Return JSON with alert_id, alert_name, service_name, metric_name, current_value, threshold_value, anomaly_description, predicted_impact, severity_estimate (low/medium/high/critical), outage_likelihood (percentage), and recommended_action.",
        "Analyze this monitoring anomaly: {webhook_payload}",
        [
          { field: "alert_id", as: "alert_id", type: "string" },
          { field: "alert_name", as: "alert_name", type: "string" },
          { field: "service_name", as: "service_name", type: "string" },
          { field: "metric_name", as: "metric_name", type: "string" },
          {
            field: "anomaly_description",
            as: "anomaly_description",
            type: "string",
          },
          { field: "predicted_impact", as: "predicted_impact", type: "string" },
          {
            field: "severity_estimate",
            as: "severity_estimate",
            type: "string",
          },
          {
            field: "outage_likelihood",
            as: "outage_likelihood",
            type: "number",
          },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
        ],
      ),
      n("3", "runCode", "Determine Escalation vs Customer Alert", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Determine if this needs internal escalation only or also customer notification
const likelihood = parseInt(vars.outage_likelihood) || 0;
const severity = (vars.severity_estimate || "").toLowerCase();

const likelyCustomerImpact = (likelihood > 60 && (severity === "high" || severity === "critical")) ||
  likelihood > 80;

const needsImmediate = severity === "critical" || likelihood > 90;

return {
  likely_customer_impact: likelyCustomerImpact,
  needs_immediate_action: needsImmediate,
  escalation_level: needsImmediate ? "P1 — immediate" : likelihood > 50 ? "P2 — urgent" : "P3 — monitor",
  alert_premium_customers: likelyCustomerImpact,
  premium_notification_email: "premium-customers@notifications.company.com"
};`,
          outputs: [
            { name: "likely_customer_impact", type: "boolean" },
            { name: "needs_immediate_action", type: "boolean" },
            { name: "escalation_level", type: "string" },
            { name: "alert_premium_customers", type: "boolean" },
            { name: "premium_notification_email", type: "string" },
          ],
        },
      }),
      n("4", "condition", "Likely Customer Impact?", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "likely_customer_impact",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
      slackNode(
        "5",
        "Alert Engineering and Support Teams",
        1000,
        ":warning: *Proactive Alert — {escalation_level}*\\n• Alert: {alert_name} ({alert_id})\\n• Service: {service_name}\\n• Anomaly: {anomaly_description}\\n• Predicted Impact: {predicted_impact}\\n• Severity Estimate: {severity_estimate}\\n• Outage Likelihood: {outage_likelihood}%\\n• Recommended Action: {recommended_action}\\n• Customer notification: {alert_premium_customers}",
      ),
      emailNode(
        "6",
        "Early Warning to Premium Customers",
        1240,
        "{premium_notification_email}",
        "Service advisory: {service_name}",
        "Hello,\\n\\nWe are proactively reaching out to let you know that our monitoring systems have detected unusual activity in {service_name}.\\n\\nWhat we know:\\n{anomaly_description}\\n\\nPotential impact: {predicted_impact}\\n\\nWhat we are doing:\\nOur engineering team is actively investigating and working to prevent any service disruption. We will provide updates as the situation develops.\\n\\nCurrent service status: https://status.company.com\\n\\nWe are reaching out early because we believe in transparent communication. No action is needed from you at this time.\\n\\nThank you,\\nSite Reliability Team",
      ),
      sheetsNode(
        "7",
        "Log Early Detection Effectiveness",
        1480,
        "ProactiveAlerts",
        '[["{alert_id}","{alert_name}","{service_name}","{severity_estimate}","{outage_likelihood}","{predicted_impact}","{likely_customer_impact}","{escalation_level}","{todays_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ─── S6 OUTAGE (21): Service Outage or Update Notification ───
  "Service Outage or Update Notification": {
    summary:
      "When a system event occurs (outage or scheduled maintenance), classifies the event type, drafts appropriate notification content, sends advance maintenance notices for planned events or incident notifications for outages, posts to both customer-facing and internal Slack channels, and logs the notification timeline.",
    nodes: [
      trigger("System Event — Outage or Maintenance", "webhook_app", {
        source: "statuspage",
        event: "incident.created",
        howItStarts:
          "Fires when a system event is created in StatusPage — either an unplanned outage or scheduled maintenance window",
        setup:
          "Configure StatusPage webhook subscription for incident and maintenance events",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Extract Event Details",
        280,
        "You are a system event extraction assistant. Parse the StatusPage webhook to find the event ID, event name, event type (outage or maintenance), status, affected components, scheduled start time (if maintenance), scheduled end time (if maintenance), description, and impact level. Return JSON regardless of payload format.",
        "Extract system event details from this StatusPage webhook: {webhook_payload}",
        [
          { field: "event_id", as: "event_id", type: "string" },
          { field: "event_name", as: "event_name", type: "string" },
          { field: "event_type", as: "event_type", type: "string" },
          { field: "status", as: "event_status", type: "string" },
          {
            field: "affected_components",
            as: "affected_components",
            type: "string",
          },
          { field: "scheduled_start", as: "scheduled_start", type: "string" },
          { field: "scheduled_end", as: "scheduled_end", type: "string" },
          { field: "description", as: "event_description", type: "string" },
          { field: "impact_level", as: "impact_level", type: "string" },
        ],
      ),
      n("3", "runCode", "Classify Event Type", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Classify as scheduled maintenance or unplanned outage
const eventType = (vars.event_type || "").toLowerCase();
const isScheduled = eventType.includes("maintenance") || eventType.includes("scheduled") || !!vars.scheduled_start;

return {
  is_scheduled_maintenance: isScheduled,
  event_category: isScheduled ? "Scheduled Maintenance" : "Service Incident",
  urgency: isScheduled ? "planned" : "immediate",
  notification_email: "affected-customers@notifications.company.com"
};`,
          outputs: [
            { name: "is_scheduled_maintenance", type: "boolean" },
            { name: "event_category", type: "string" },
            { name: "urgency", type: "string" },
            { name: "notification_email", type: "string" },
          ],
        },
      }),
      aiNode(
        "4",
        "Draft Notification by Event Type",
        760,
        "You are a service communications specialist. Based on the event type (scheduled maintenance vs unplanned outage), draft an appropriate customer notification. For maintenance: include the window, what will be affected, and what customers should do. For outages: include what is happening, current status, and next update timeline. Be clear, professional, and empathetic. Return JSON with email_subject and email_body.",
        "Event: {event_name}. Type: {event_category}. Components: {affected_components}. Description: {event_description}. Impact: {impact_level}. Scheduled: {scheduled_start} to {scheduled_end}. Status: {event_status}. Draft notification.",
        [
          {
            field: "email_subject",
            as: "notification_subject",
            type: "string",
          },
          { field: "email_body", as: "notification_body", type: "string" },
        ],
      ),
      n("5", "condition", "Scheduled Maintenance?", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "is_scheduled_maintenance",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
      emailNode(
        "6",
        "Send Advance Maintenance Notice",
        1240,
        "{notification_email}",
        "[Maintenance] {notification_subject}",
        "{notification_body}",
      ),
      emailNode(
        "7",
        "Send Incident Notification",
        1240,
        "{notification_email}",
        "[Incident] {notification_subject}",
        "{notification_body}",
        400,
      ),
      slackNode(
        "8",
        "Post to Customer and Internal Channels",
        1480,
        ":mega: *{event_category}: {event_name}*\\n• ID: {event_id}\\n• Status: {event_status}\\n• Impact: {impact_level}\\n• Components: {affected_components}\\n• Type: {urgency}\\n• Window: {scheduled_start} — {scheduled_end}\\n• Description: {event_description}\\n• Customer notification: Sent",
      ),
      sheetsNode(
        "9",
        "Log Notification Timeline",
        1720,
        "ServiceNotifications",
        '[["{event_id}","{event_name}","{event_category}","{impact_level}","{affected_components}","{urgency}","{event_status}","notified","{todays_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "8"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ─── S7 COMMUNITY (22): Community Forum Auto Responder ───
  "Community Forum Auto Responder": {
    summary:
      "When a new forum post is created, AI classifies the post type (question, discussion, bug report, feature request), searches Pinecone for existing answers and related threads, generates a helpful response with source citations and confidence score, auto-posts high-confidence responses with a bot label, and flags low-confidence posts for the community manager via Slack.",
    nodes: [
      trigger("New Forum Post Created", "webhook_app", {
        source: "discourse",
        event: "post.created",
        howItStarts: "Fires when a new post is created in the community forum",
        setup:
          "Configure Discourse webhook for post_created events. Set content type to application/json.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Classify Post Type",
        280,
        "You are a community forum classifier. Parse the webhook payload to extract the post title, post body, author username, author email, category, tags, and post ID. Then classify the post type as: question, discussion, bug_report, or feature_request. Return JSON regardless of payload format.",
        "Classify this forum post from the webhook: {webhook_payload}",
        [
          { field: "post_id", as: "post_id", type: "string" },
          { field: "post_title", as: "post_title", type: "string" },
          { field: "post_body", as: "post_body", type: "string" },
          { field: "author_username", as: "author_username", type: "string" },
          { field: "author_email", as: "author_email", type: "string" },
          { field: "category", as: "post_category", type: "string" },
          { field: "post_type", as: "post_type", type: "string" },
        ],
      ),
      n("3", "hitApi", "Search Pinecone for Existing Answers", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://{pinecone_index}-{pinecone_project}.svc.{pinecone_env}.pinecone.io/query",
          headers: [
            { key: "Api-Key", value: "{pinecone_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "vector", value: "{post_embedding}" },
            { key: "topK", value: "5" },
            { key: "includeMetadata", value: "true" },
            { key: "namespace", value: "community_posts" },
          ],
          variables: [
            { name: "matches", customName: "related_threads", type: "object" },
            {
              name: "matches[0].score",
              customName: "top_match_score",
              type: "number",
            },
            {
              name: "matches[0].metadata.title",
              customName: "top_match_title",
              type: "string",
            },
            {
              name: "matches[0].metadata.answer",
              customName: "top_match_answer",
              type: "string",
            },
            {
              name: "matches[0].metadata.url",
              customName: "top_match_url",
              type: "string",
            },
          ],
        },
      }),
      aiNode(
        "4",
        "Generate Response with Citations",
        760,
        "You are a helpful community forum bot. Using the related threads and existing answers found, generate a helpful response to the forum post. Include citations to existing threads where relevant. Rate your confidence (0.0-1.0) in the response accuracy. For questions: provide a direct answer. For bug reports: acknowledge and provide known workarounds. For feature requests: link to existing discussions. For discussions: add relevant context. Return JSON with response_text, confidence, and sources_cited.",
        "Post type: {post_type}. Title: {post_title}. Body: {post_body}. Top match (score {top_match_score}): {top_match_title} — {top_match_answer} (URL: {top_match_url}). Generate a helpful response with citations.",
        [
          { field: "response_text", as: "response_text", type: "string" },
          { field: "confidence", as: "response_confidence", type: "number" },
          { field: "sources_cited", as: "sources_cited", type: "string" },
        ],
      ),
      n("5", "condition", "Confidence > 0.85?", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "response_confidence",
              operator: "greaterThan",
              value: "0.85",
            },
          ],
        },
      }),
      n("6", "hitApi", "Post Auto-Response on Forum", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{discourse_url}/posts.json",
          headers: [
            { key: "Api-Key", value: "{discourse_api_key}" },
            { key: "Api-Username", value: "omniengage-bot" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "topic_id", value: "{post_id}" },
            {
              key: "raw",
              value:
                "{response_text}\\n\\n---\\n*This response was generated by OmniEngage Bot. Sources: {sources_cited}*",
            },
          ],
          variables: [
            { name: "id", customName: "reply_post_id", type: "string" },
          ],
        },
      }),
      slackNode(
        "7",
        "Flag Low-Confidence Posts for Community Manager",
        1240,
        ":speech_balloon: *Forum Post Needs Human Review*\\n• Post: {post_title} (ID: {post_id})\\n• Type: {post_type}\\n• Author: {author_username}\\n• Category: {post_category}\\n• AI Confidence: {response_confidence}\\n• Draft response available but below confidence threshold\\n• Please review and respond: https://{discourse_url}/t/{post_id}",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
    ],
  },

  // ─── S7 COMMUNITY (23): Community Moderation Queue ───
  "Community Moderation Queue": {
    summary:
      "When new forum content is submitted, AI classifies it for toxicity, spam, policy violations, and off-topic content, applies moderation rules to determine action, hides violating content and notifies the author, sends borderline cases to the moderator queue via Slack, approves clean content, and logs moderation actions and accuracy.",
    nodes: [
      trigger("New Forum Content Submitted", "webhook_app", {
        source: "discourse",
        event: "post.created",
        howItStarts:
          "Fires when new content is submitted to the community forum for moderation",
        setup:
          "Configure Discourse webhook for post_created events to run content through moderation before publishing",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Classify Content for Violations",
        280,
        "You are a content moderation AI. Analyze the submitted content for: toxicity (score 0-1), spam likelihood (score 0-1), policy violation (boolean), off-topic (boolean), and specific violation type if any (harassment, hate_speech, spam, self_promotion, off_topic, none). Also extract the post ID, author username, post title, and post body. Return JSON regardless of payload format.",
        "Analyze this forum submission for content violations: {webhook_payload}",
        [
          { field: "post_id", as: "post_id", type: "string" },
          { field: "author_username", as: "author_username", type: "string" },
          { field: "author_email", as: "author_email", type: "string" },
          { field: "post_title", as: "post_title", type: "string" },
          { field: "post_body", as: "post_body", type: "string" },
          { field: "toxicity_score", as: "toxicity_score", type: "number" },
          { field: "spam_score", as: "spam_score", type: "number" },
          {
            field: "policy_violation",
            as: "policy_violation",
            type: "boolean",
          },
          { field: "off_topic", as: "off_topic", type: "boolean" },
          { field: "violation_type", as: "violation_type", type: "string" },
        ],
      ),
      n("3", "runCode", "Apply Moderation Rules and Determine Action", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Apply moderation rules
const toxicity = parseFloat(vars.toxicity_score) || 0;
const spam = parseFloat(vars.spam_score) || 0;
const policyViolation = vars.policy_violation === true || vars.policy_violation === "true";
const offTopic = vars.off_topic === true || vars.off_topic === "true";

let action = "approve";
let reason = "Content meets community guidelines";
let violationDetected = false;

if (toxicity > 0.8 || policyViolation) {
  action = "hide";
  violationDetected = true;
  reason = "Content flagged for: " + (vars.violation_type || "policy violation") + " (toxicity: " + toxicity + ")";
} else if (spam > 0.8) {
  action = "hide";
  violationDetected = true;
  reason = "Content flagged as spam (score: " + spam + ")";
} else if (toxicity > 0.5 || spam > 0.5 || offTopic) {
  action = "review";
  reason = "Borderline content needs human review (toxicity: " + toxicity + ", spam: " + spam + ", off-topic: " + offTopic + ")";
} else {
  action = "approve";
}

return {
  moderation_action: action,
  moderation_reason: reason,
  violation_detected: violationDetected,
  needs_review: action === "review"
};`,
          outputs: [
            { name: "moderation_action", type: "string" },
            { name: "moderation_reason", type: "string" },
            { name: "violation_detected", type: "boolean" },
            { name: "needs_review", type: "boolean" },
          ],
        },
      }),
      n("4", "condition", "Violation Detected?", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "violation_detected",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
      n("5", "hitApi", "Hide Content and Notify Author", 1000, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{discourse_url}/posts/{post_id}/hidden.json",
          headers: [
            { key: "Api-Key", value: "{discourse_api_key}" },
            { key: "Api-Username", value: "system" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "post_action_type_id", value: "4" },
            { key: "flag_topic", value: "false" },
          ],
          variables: [
            { name: "success", customName: "content_hidden", type: "boolean" },
          ],
        },
      }),
      slackNode(
        "6",
        "Send to Moderator Queue for Review",
        1000,
        ":mag: *Moderation Review Needed*\\n• Post: {post_title} (ID: {post_id})\\n• Author: {author_username}\\n• Reason: {moderation_reason}\\n• Toxicity: {toxicity_score} | Spam: {spam_score}\\n• Content preview: {post_body}\\n• Action needed: Approve or reject\\n• Review: https://{discourse_url}/review",
        400,
      ),
      n(
        "7",
        "hitApi",
        "Approve Content for Publishing",
        1240,
        {
          hitApiDetails: {
            method: "PUT",
            url: "https://{discourse_url}/posts/{post_id}.json",
            headers: [
              { key: "Api-Key", value: "{discourse_api_key}" },
              { key: "Api-Username", value: "system" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "post",
                value: '{"edit_reason":"Auto-approved by moderation system"}',
              },
            ],
            variables: [
              {
                name: "post.id",
                customName: "approved_post_id",
                type: "string",
              },
            ],
          },
        },
        400,
      ),
      sheetsNode(
        "8",
        "Log Moderation Actions and Accuracy",
        1480,
        "ModerationLog",
        '[["{post_id}","{author_username}","{post_title}","{toxicity_score}","{spam_score}","{violation_type}","{moderation_action}","{moderation_reason}","{todays_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "8"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ─── S7 COMMUNITY (24): Community-Support Invite ───
  "Community-Support Invite": {
    summary:
      "Runs monthly to find highly engaged customers with good CSAT scores and support champion potential from HubSpot, scores them for community advocate fit, sends personalized community invitations to high-scoring candidates (>80), notifies the community manager of new invites via Slack, and tracks invite acceptance rates.",
    nodes: [
      trigger("Monthly Community Invite Check", "schedule", {
        schedule: "First Monday of every month at 10:00 AM",
        howItStarts:
          "Runs monthly to identify potential community advocates among highly engaged customers",
        setup: "No external setup needed — runs on schedule",
        inputFields: [],
      }),
      n("2", "hitApi", "Fetch High-Engagement Customers from HubSpot", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"engagement_score","operator":"GTE","value":"70"},{"propertyName":"csat_score","operator":"GTE","value":"4"}]}]',
            },
            {
              key: "properties",
              value:
                '["email","firstname","lastname","engagement_score","csat_score","support_tickets_created","support_tickets_resolved_by_community","product_usage_score","account_age_days"]',
            },
            { key: "limit", value: "50" },
          ],
          variables: [
            {
              name: "results",
              customName: "champion_candidates",
              type: "object",
            },
            { name: "total", customName: "total_candidates", type: "number" },
          ],
        },
      }),
      n("3", "runCode", "Score Candidates for Community Advocate Fit", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Score each candidate for community advocate potential
const candidates = vars.champion_candidates || [];
const candidate = candidates[0] || {};
const props = candidate.properties || {};

const engagement = parseInt(props.engagement_score) || 0;
const csat = parseFloat(props.csat_score) || 0;
const ticketsCreated = parseInt(props.support_tickets_created) || 0;
const communityResolutions = parseInt(props.support_tickets_resolved_by_community) || 0;
const usage = parseInt(props.product_usage_score) || 0;
const accountAge = parseInt(props.account_age_days) || 0;

// Weighted scoring
let score = 0;
score += Math.min(engagement, 100) * 0.25; // 25% engagement
score += (csat / 5 * 100) * 0.20; // 20% satisfaction
score += Math.min(communityResolutions * 10, 100) * 0.20; // 20% community helping
score += Math.min(usage, 100) * 0.15; // 15% product usage
score += Math.min(accountAge / 365 * 100, 100) * 0.10; // 10% tenure
score += Math.min(ticketsCreated * 5, 100) * 0.10; // 10% product knowledge

score = Math.round(score);

return {
  candidate_email: props.email || "",
  candidate_name: (props.firstname || "") + " " + (props.lastname || ""),
  advocate_score: score,
  engagement_score: engagement,
  csat_score: csat,
  community_contributions: communityResolutions,
  qualifies: score > 80
};`,
          outputs: [
            { name: "candidate_email", type: "string" },
            { name: "candidate_name", type: "string" },
            { name: "advocate_score", type: "number" },
            { name: "engagement_score", type: "number" },
            { name: "csat_score", type: "number" },
            { name: "community_contributions", type: "number" },
            { name: "qualifies", type: "boolean" },
          ],
        },
      }),
      n("4", "condition", "Advocate Score > 80?", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "advocate_score",
              operator: "greaterThan",
              value: "80",
            },
          ],
        },
      }),
      emailNode(
        "5",
        "Send Personalized Community Invite",
        1000,
        "{candidate_email}",
        "You are invited to join our Community Champions program",
        "Hi {candidate_name},\\n\\nWe have noticed your incredible engagement and contributions to our community, and we would love to invite you to join our Community Champions program!\\n\\nAs a Community Champion, you will get:\\n• Early access to new features and beta programs\\n• Direct line to our product team for feedback\\n• Exclusive community events and AMAs\\n• Champion badge on your profile\\n• Annual Champions Summit invitation\\n\\nYour engagement score of {engagement_score} and {community_contributions} community contributions make you a perfect fit.\\n\\n[Accept Invitation →]\\n\\nWe are excited about the possibility of having you on board!\\n\\nBest,\\nCommunity Team",
      ),
      slackNode(
        "6",
        "Notify Community Manager of New Invites",
        1240,
        ":star: *New Community Champion Invite Sent*\\n• Candidate: {candidate_name} ({candidate_email})\\n• Advocate Score: {advocate_score}/100\\n• Engagement: {engagement_score} | CSAT: {csat_score}\\n• Community Contributions: {community_contributions}\\n• Total candidates this month: {total_candidates}",
      ),
      sheetsNode(
        "7",
        "Track Invite Acceptance Rates",
        1480,
        "CommunityInvites",
        '[["{candidate_name}","{candidate_email}","{advocate_score}","{engagement_score}","{csat_score}","{community_contributions}","invited","{todays_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ─── S7 COMMUNITY (25): Knowledge Base Article Suggester ───
  "Knowledge Base Article Suggester": {
    summary:
      "When an agent is handling a ticket, AI analyzes the ticket content and identifies relevant KB articles, searches the knowledge base via Pinecone by issue description, ranks articles by relevance and recency, sends high-relevance suggestions to the agent sidebar via Slack, and tracks article suggestion effectiveness.",
    nodes: [
      trigger("Agent Handling Ticket", "webhook_app", {
        source: "zendesk",
        event: "ticket.assigned",
        howItStarts:
          "Fires when a support ticket is assigned to an agent for handling",
        setup: "Configure Zendesk webhook trigger for ticket assignment events",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Analyze Ticket and Identify Relevant Topics",
        280,
        "You are a support ticket analysis assistant. Parse the webhook to extract the ticket ID, ticket subject, ticket description, customer email, customer name, assigned agent, ticket priority, and identify the key topics/issues described. Return JSON with search_query (a distilled search query for KB lookup) regardless of payload format.",
        "Analyze this support ticket for KB article matching: {webhook_payload}",
        [
          { field: "ticket_id", as: "ticket_id", type: "string" },
          { field: "ticket_subject", as: "ticket_subject", type: "string" },
          {
            field: "ticket_description",
            as: "ticket_description",
            type: "string",
          },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "assigned_agent", as: "assigned_agent", type: "string" },
          { field: "ticket_priority", as: "ticket_priority", type: "string" },
          { field: "search_query", as: "kb_search_query", type: "string" },
        ],
      ),
      n("3", "hitApi", "Search Knowledge Base via Pinecone", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://{pinecone_index}-{pinecone_project}.svc.{pinecone_env}.pinecone.io/query",
          headers: [
            { key: "Api-Key", value: "{pinecone_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "vector", value: "{kb_query_embedding}" },
            { key: "topK", value: "5" },
            { key: "includeMetadata", value: "true" },
            { key: "namespace", value: "knowledge_base" },
          ],
          variables: [
            { name: "matches", customName: "kb_matches", type: "object" },
            {
              name: "matches[0].score",
              customName: "top_article_score",
              type: "number",
            },
            {
              name: "matches[0].metadata.title",
              customName: "top_article_title",
              type: "string",
            },
            {
              name: "matches[0].metadata.url",
              customName: "top_article_url",
              type: "string",
            },
            {
              name: "matches[0].metadata.summary",
              customName: "top_article_summary",
              type: "string",
            },
            {
              name: "matches[0].metadata.updated_at",
              customName: "top_article_updated",
              type: "string",
            },
          ],
        },
      }),
      n("4", "runCode", "Rank Articles by Relevance and Recency", 760, {
        codeDetails: {
          language: "javascript",
          code: `// Rank and format KB article suggestions
const matches = vars.kb_matches || [];
const topScore = parseFloat(vars.top_article_score) || 0;

const highRelevance = topScore > 0.8;
const suggestions = [];

for (let i = 0; i < Math.min(matches.length, 3); i++) {
  const m = matches[i] || {};
  const meta = m.metadata || {};
  suggestions.push({
    rank: i + 1,
    title: meta.title || "Unknown",
    url: meta.url || "",
    score: (m.score || 0).toFixed(2),
    summary: meta.summary || "",
    updated: meta.updated_at || ""
  });
}

const formattedSuggestions = suggestions.map(s =>
  s.rank + ". " + s.title + " (relevance: " + s.score + ")\\n   " + s.summary + "\\n   " + s.url
).join("\\n\\n");

return {
  has_high_relevance: highRelevance,
  suggestion_count: suggestions.length,
  formatted_suggestions: formattedSuggestions,
  top_relevance_score: topScore
};`,
          outputs: [
            { name: "has_high_relevance", type: "boolean" },
            { name: "suggestion_count", type: "number" },
            { name: "formatted_suggestions", type: "string" },
            { name: "top_relevance_score", type: "number" },
          ],
        },
      }),
      n("5", "condition", "High-Relevance Article Found?", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "has_high_relevance",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
      slackNode(
        "6",
        "Send Article Suggestions to Agent",
        1240,
        ":books: *KB Article Suggestions for Ticket #{ticket_id}*\\n• Subject: {ticket_subject}\\n• Agent: {assigned_agent}\\n• Top relevance: {top_relevance_score}\\n\\nSuggested articles:\\n{formatted_suggestions}\\n\\nUse these to help resolve the ticket faster!",
      ),
      sheetsNode(
        "7",
        "Track Article Suggestion Effectiveness",
        1480,
        "KBSuggestions",
        '[["{ticket_id}","{ticket_subject}","{assigned_agent}","{kb_search_query}","{top_article_title}","{top_relevance_score}","{suggestion_count}","{has_high_relevance}","{todays_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
    ],
  },

  // ─── S7 COMMUNITY (26): Knowledge Base Personal Wiki ───
  "Knowledge Base Personal Wiki": {
    summary:
      "When an agent saves an internal note or solution, AI extracts reusable knowledge from the note, checks for duplicate or overlapping wiki entries, creates a new wiki entry with source attribution and tags for novel knowledge, notifies the knowledge team via Slack for review, and tracks wiki growth and usage.",
    nodes: [
      trigger("Agent Saves Internal Note", "webhook_app", {
        source: "zendesk",
        event: "ticket.comment.internal",
        howItStarts:
          "Fires when an agent saves an internal note or documented solution on a support ticket",
        setup:
          "Configure Zendesk webhook trigger for internal comment creation events",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Extract Reusable Knowledge from Note",
        280,
        "You are a knowledge extraction specialist. Parse the webhook payload to find the ticket ID, agent name, agent email, note content, and ticket subject. Then analyze the note to extract reusable knowledge: the problem described, the solution steps, applicable product areas, and suggested tags. Rate the knowledge value (0-1). Return JSON regardless of payload format.",
        "Extract reusable knowledge from this agent note: {webhook_payload}",
        [
          { field: "ticket_id", as: "ticket_id", type: "string" },
          { field: "agent_name", as: "agent_name", type: "string" },
          { field: "agent_email", as: "agent_email", type: "string" },
          { field: "ticket_subject", as: "ticket_subject", type: "string" },
          {
            field: "problem_description",
            as: "problem_description",
            type: "string",
          },
          { field: "solution_steps", as: "solution_steps", type: "string" },
          { field: "product_area", as: "product_area", type: "string" },
          { field: "suggested_tags", as: "suggested_tags", type: "string" },
          { field: "knowledge_value", as: "knowledge_value", type: "number" },
        ],
      ),
      n("3", "runCode", "Check for Duplicate Wiki Entries", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Simple duplicate detection based on problem description similarity
const problem = (vars.problem_description || "").toLowerCase();
const productArea = (vars.product_area || "").toLowerCase();
const knowledgeValue = parseFloat(vars.knowledge_value) || 0;

// For production, this would query the wiki/vector DB
// Here we apply basic heuristics
const isNovel = knowledgeValue > 0.5;
const hasSubstance = problem.length > 20 && (vars.solution_steps || "").length > 30;

return {
  is_new_knowledge: isNovel && hasSubstance,
  duplicate_check: isNovel ? "No close duplicates found" : "Similar entries may exist — flagged for review",
  wiki_title: (vars.product_area || "General") + ": " + (vars.problem_description || "").substring(0, 80),
  wiki_tags: vars.suggested_tags || productArea
};`,
          outputs: [
            { name: "is_new_knowledge", type: "boolean" },
            { name: "duplicate_check", type: "string" },
            { name: "wiki_title", type: "string" },
            { name: "wiki_tags", type: "string" },
          ],
        },
      }),
      n("4", "condition", "New Knowledge?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "is_new_knowledge", operator: "equals", value: "true" },
          ],
        },
      }),
      n("5", "hitApi", "Create Wiki Entry with Attribution", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://{confluence_url}/wiki/rest/api/content",
          headers: [
            { key: "Authorization", value: "Basic {confluence_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "type", value: "page" },
            { key: "title", value: "{wiki_title}" },
            { key: "space", value: '{"key":"SUPPORT"}' },
            {
              key: "body",
              value:
                '{"storage":{"value":"<h2>Problem</h2><p>{problem_description}</p><h2>Solution</h2><p>{solution_steps}</p><h2>Product Area</h2><p>{product_area}</p><hr/><p><em>Source: Ticket #{ticket_id} — Contributed by {agent_name}</em></p>","representation":"storage"}}',
            },
            {
              key: "metadata",
              value: '{"labels":[{"prefix":"global","name":"{wiki_tags}"}]}',
            },
          ],
          variables: [
            { name: "id", customName: "wiki_page_id", type: "string" },
            {
              name: "_links.webui",
              customName: "wiki_page_url",
              type: "string",
            },
          ],
        },
      }),
      slackNode(
        "6",
        "Notify Knowledge Team of New Entry",
        1240,
        ":notebook: *New Wiki Entry for Review*\\n• Title: {wiki_title}\\n• Product Area: {product_area}\\n• Tags: {wiki_tags}\\n• Contributed by: {agent_name}\\n• Source: Ticket #{ticket_id}\\n• Duplicate check: {duplicate_check}\\n• Review: {wiki_page_url}",
      ),
      sheetsNode(
        "7",
        "Track Wiki Growth and Usage",
        1480,
        "WikiGrowth",
        '[["{wiki_title}","{product_area}","{agent_name}","{ticket_id}","{wiki_tags}","{knowledge_value}","{is_new_knowledge}","{todays_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ─── S7 COMMUNITY (27): Support Ticket to Community Post ───
  "Support Ticket to Community Post": {
    summary:
      "When a ticket is resolved with a reusable solution, AI anonymizes customer details and extracts the solution, code verifies PII removal and content quality, routes high-quality content (score >0.8) through knowledge manager approval, publishes to the community forum as a solved post, notifies the support team via Slack, and tracks community post engagement.",
    nodes: [
      trigger("Ticket Resolved with Reusable Solution", "webhook_app", {
        source: "zendesk",
        event: "ticket.solved",
        howItStarts:
          "Fires when a support ticket is resolved and tagged as having a reusable solution",
        setup:
          "Configure Zendesk webhook trigger for ticket solved events with the tag 'reusable_solution'",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Anonymize and Extract Solution",
        280,
        "You are a content preparation specialist. Parse the webhook to extract the ticket ID, ticket subject, problem description, solution steps, agent name, product area, and any code snippets. Anonymize ALL customer-identifying information (names, emails, account IDs, company names) — replace with generic placeholders. Return JSON with the anonymized content, a quality_score (0-1), and a community_title.",
        "Anonymize customer details and extract reusable solution from this resolved ticket: {webhook_payload}",
        [
          { field: "ticket_id", as: "ticket_id", type: "string" },
          { field: "community_title", as: "community_title", type: "string" },
          { field: "problem_description", as: "anon_problem", type: "string" },
          { field: "solution_steps", as: "anon_solution", type: "string" },
          { field: "product_area", as: "product_area", type: "string" },
          { field: "agent_name", as: "agent_name", type: "string" },
          { field: "quality_score", as: "quality_score", type: "number" },
          { field: "tags", as: "content_tags", type: "string" },
        ],
      ),
      n("3", "runCode", "Verify PII Removal and Content Quality", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Verify PII has been removed and content meets quality standards
const problem = vars.anon_problem || "";
const solution = vars.anon_solution || "";
const quality = parseFloat(vars.quality_score) || 0;

// Check for common PII patterns
const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}/;
const phoneRegex = /\\b\\d{3}[-.]?\\d{3}[-.]?\\d{4}\\b/;
const namePatterns = /\\b(Mr\\.|Mrs\\.|Ms\\.|Dr\\.)\\s+[A-Z][a-z]+/;

const hasPII = emailRegex.test(problem + solution) ||
  phoneRegex.test(problem + solution) ||
  namePatterns.test(problem + solution);

const hasSubstance = problem.length > 50 && solution.length > 100;
const finalQuality = hasPII ? 0 : (hasSubstance ? quality : quality * 0.5);

return {
  pii_clean: !hasPII,
  content_quality: Math.round(finalQuality * 100) / 100,
  quality_passed: finalQuality > 0.8,
  pii_warning: hasPII ? "PII detected — content needs re-anonymization" : "No PII detected",
  word_count: (problem + " " + solution).split(/\\s+/).length
};`,
          outputs: [
            { name: "pii_clean", type: "boolean" },
            { name: "content_quality", type: "number" },
            { name: "quality_passed", type: "boolean" },
            { name: "pii_warning", type: "string" },
            { name: "word_count", type: "number" },
          ],
        },
      }),
      n("4", "condition", "Quality Score > 0.8?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "quality_passed", operator: "equals", value: "true" },
          ],
        },
      }),
      n("5", "approvalGate", "Knowledge Manager Reviews Before Publish", 1000, {
        approvalDetails: {
          approver: "knowledge_manager",
          timeout: 72,
          timeoutUnit: "hours",
        },
      }),
      n("6", "hitApi", "Publish to Community Forum as Solved Post", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{discourse_url}/posts.json",
          headers: [
            { key: "Api-Key", value: "{discourse_api_key}" },
            { key: "Api-Username", value: "omniengage-bot" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "[Solved] {community_title}" },
            {
              key: "raw",
              value:
                "## Problem\\n\\n{anon_problem}\\n\\n## Solution\\n\\n{anon_solution}\\n\\n---\\n*Product Area: {product_area} | Tags: {content_tags}*\\n*This solution was contributed by our support team.*",
            },
            { key: "category", value: "5" },
            { key: "tags[]", value: "{content_tags}" },
          ],
          variables: [
            { name: "id", customName: "community_post_id", type: "string" },
            {
              name: "topic_slug",
              customName: "community_post_slug",
              type: "string",
            },
          ],
        },
      }),
      slackNode(
        "7",
        "Notify Support Team of New Community Resource",
        1480,
        ":tada: *New Community Post Published*\\n• Title: [Solved] {community_title}\\n• Product Area: {product_area}\\n• Source: Ticket #{ticket_id}\\n• Contributed by: {agent_name}\\n• Quality Score: {content_quality}\\n• Word Count: {word_count}\\n• View: https://{discourse_url}/t/{community_post_slug}/{community_post_id}",
      ),
      sheetsNode(
        "8",
        "Track Community Post Engagement",
        1720,
        "CommunityPosts",
        '[["{community_post_id}","{community_title}","{product_area}","{ticket_id}","{agent_name}","{content_quality}","{word_count}","{content_tags}","published","{todays_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },
};
